/**
 * PMD Ads Frontend JavaScript
 *
 * Handles GPT ad initialization and display.
 */

( function( window, document ) {
	'use strict';

	var TARGETING_KEY_REFRESH = 'refresh';
	var TARGETING_KEY_SLOT = 'slot';
	var TARGETING_KEY_TOPICS = 'topics';
	var TARGETING_KEY_AD_SLOT = 'ad_slot';
	var TARGETING_KEY_AD_FINGERPRINT = 'ad_cfp';
	var TARGETING_KEY_REFERRER = 'r';
	var TARGETING_KEY_LAYOUT = 'layout';
	const TARGETING_KEY_SESSION_PAGE_VIEWS = 'spv';
	const TARGETING_KEY_USER_TYPE = 'ut';
	// User Type (`ut`) page-level KV values. Obfuscated per VIP-351 so no PII
	// leaves the browser — only the audience tier is sent to GAM.
	const USER_TYPE_ANONYMOUS = '1';
	const USER_TYPE_REGISTERED = '2';
	const USER_TYPE_SUBSCRIBER = '3';
	// pmd-paywall (Naviga Engage) publishes the authenticated user tier on
	// window.pmdNaviga.state, sets window.pmdNaviga.userAccessLevelConfirmed to true
	// once Engage has made its terminal account-status decision, and fires this event
	// on every change. See pmd-paywall/assets/js/frontend/{prepaint,loader}.js.
	const PAYWALL_STATE_REGISTERED = 'registered';
	const PAYWALL_STATE_SUBSCRIBED = 'subscribed';
	const PAYWALL_STATE_CHANGE_EVENT = 'pmd:naviga-state-change';
	// Max time to wait for Engage's terminal decision before giving up. Only
	// registered (logged-in) users ever wait — anonymous and already-confirmed
	// states resolve synchronously, so ads are never delayed for the anonymous
	// majority. The subscriber promotion (registered → subscribed) normally lands
	// well within this bound, but on a cache-warm refresh the ad stack starts this
	// timer at ~0ms while Engage still needs uncached network roundtrips, so a 3s
	// cap raced the meter and mislabelled subscribers as ut=2 (VIP-351). The wider
	// bound covers that skew; if it still times out unconfirmed we omit `ut`
	// rather than emit a wrong value (see applyUserTypeTargeting).
	const USER_TYPE_RESOLVE_TIMEOUT_MS = 8000;
	var PAGE_LEVEL_ONLY_TARGETING_KEYS = {};
	PAGE_LEVEL_ONLY_TARGETING_KEYS[ TARGETING_KEY_TOPICS ] = true;
	PAGE_LEVEL_ONLY_TARGETING_KEYS[ TARGETING_KEY_LAYOUT ] = true;
	PAGE_LEVEL_ONLY_TARGETING_KEYS[ TARGETING_KEY_SESSION_PAGE_VIEWS ] = true;
	PAGE_LEVEL_ONLY_TARGETING_KEYS[ TARGETING_KEY_USER_TYPE ] = true;
	// Session page-view (spv) counter — bucketed engagement KV (VIP-353).
	// Stored in localStorage so the count is shared across every tab of the same
	// origin, and anchored to the Amplitude analytics session id so it resets when
	// analytics starts a new session. SPV_SESSION_TIMEOUT_MS is a fallback
	// inactivity window used only while the Amplitude SDK is still loading.
	const SPV_STORAGE_KEY = 'pmd_session_page_views';
	const SPV_SESSION_TIMEOUT_MS = 30 * 60 * 1000; // Matches Amplitude's default session timeout.
	var SLOT_TYPE_DISPLAY = 'display';
	var SLOT_TYPE_INFEED = 'infeed';
	// Match ViewportTier::BP_TABLET / BP_DESKTOP so native ads stay aligned with the inline head-script tier.
	var LAYOUT_BP_TABLET = 768;
	var LAYOUT_BP_DESKTOP = 1280;
	var LAYOUT_TIER_MOBILE = 'mobile';
	var LAYOUT_TIER_TABLET = 'tablet';
	var LAYOUT_TIER_DESKTOP = 'desktop';
	// In-content ad size-mapping bounds. Mirrors ContentAdInjector::BP_CONTENT_LG /
	// BP_CONTENT_MD; the md bound is overridden per slot via slotConfig.mdBreakpoint
	// (960 on v2 story pages, where the right-rail sizes only apply above 960px).
	var CONTENT_BP_LG = 1200;
	var CONTENT_BP_MD = 729;
	var UTM_TARGETING_KEYS = [
		'utm_campaign',
		'utm_source',
		'utm_medium',
		'utm_name',
		'utm_term',
		'utm_content'
	];

	// Initialize namespace.
	window.pmdAds = window.pmdAds || {};

	/**
	 * Main ads controller.
	 */
	window.pmdAds.controller = {
		initialized: false,
		config: null,
		contentSlots: null,
		definedSlots: [],
		renderedSlots: [],
		slotRefreshStates: {},
		slotRefreshIntervals: {},
		refreshVisibilityHandlersBound: false,
		interscrollerRefreshBlockedSlots: {},
		inContentHeightObservers: {},
		locMap: {},
		adSlotMap: {},
		viafouraAdsReady: false,
		viafouraAdQueue: [],
		viafouraAdCount: 0,
		utmTargeting: null,
		referrerTargetingResolved: false,
		referrerTargeting: '',
		perfTimings: null,

		/**
		 * Initialize the ads controller.
		 */
		init: function() {
			if ( this.initialized ) {
				return;
			}

			this.config = window.pmdAdsConfig || {};
			this.contentSlots = window.pmdContentAdSlots || {};
			this.slotRefreshIntervals = this.buildSlotRefreshIntervalMap();
			this.initialized = true;
			this.bindUserTypeTargetingUpdates();

			// Subscribe before Viafoura loads. Requests that arrive before the
			// first display are queued until GPT services are enabled.
			this.bindViafouraAds();

			// Initialize perf tracking only in debug mode.
			if ( this.isDebugMode() ) {
				this.perfTimings = {};
				this.perfMark( 'init-start' );
			}

			// Initialize content ad placeholders first.
			this.initContentAdPlaceholders();

			// JW Player binding has no functional dependency on GPT — kick it off
			// immediately so video ad scheduling does not wait on the waitForGPT
			// poll. Errors here can't break display ad targeting (the bind is in
			// its own try-path inside videoAdEvents).
			this.initVideoAdScheduling();

			this.waitForGPT( function() {
				this.perfMark( 'gpt-ready' );
				this.setupGPT();
				this.buildLocMap();
				this.buildAdSlotMap();
				this.defineAllSlots();
				this.perfMark( 'slots-defined' );

				// Run targeting and header bidding in parallel, then proceed once both are done.
				var self = this;
				var targetingDone = false;
				var biddingDone = false;

				function checkReady() {
					if ( targetingDone && biddingDone ) {
						self.perfMark( 'display-start' );
						self.enableServices();
						self.trackRenderedSlots();
						self.displayAllSlots();
						self.flushViafouraAdRequests();
						self.initDebugOverlays();
						self.initPerfSlotTracking();
						self.startAdRefresh();
					}
				}

				this.applyTargeting( function() {
					targetingDone = true;
					self.perfMark( 'targeting-done' );
					checkReady();
				} );

				this.runHeaderBiddingAuction( function() {
					biddingDone = true;
					self.perfMark( 'bidding-done' );
					checkReady();
				}.bind( this ) );
			}.bind( this ) );
		},

		/**
		 * Run header bidding auction if enabled.
		 *
		 * @param {Function} callback Callback when auction is complete.
		 */
		runHeaderBiddingAuction: function( callback ) {
			var self = this;
			var headerBiddingConfig = this.config.headerBidding || {};

			// Skip if header bidding is not enabled.
			if ( ! headerBiddingConfig.enabled ) {
				callback();
				return;
			}

			// Check if header bidding module is available.
			if ( ! window.pmdAds.headerBidding ) {
				callback();
				return;
			}

			// Build slots array for header bidding.
			var allSlots = this.buildSlotsForHeaderBidding();

			if ( ! allSlots.length ) {
				callback();
				return;
			}

			// Run the auction.
			window.pmdAds.headerBidding.runAuction( allSlots, callback );
		},

		/**
		 * Build slots configuration for header bidding in DOM order.
		 *
		 * Uses the same lookup-map + single-DOM-query pattern as
		 * defineAllSlots() so the bidding array matches the SRA order.
		 *
		 * @return {Array} Array of slot configurations.
		 */
		buildSlotsForHeaderBidding: function() {
			var self = this;
			var slots = [];
			var configSlots = this.config.slots || [];
			var contentSlots = this.contentSlots || {};
			var oopSlot = this.config.oopSlot || null;

			// Build O(1) lookup map for config slots by divId.
			var configSlotMap = {};
			configSlots.forEach( function( slotConfig ) {
				if ( slotConfig.divId ) {
					configSlotMap[ slotConfig.divId ] = slotConfig;
				}
			} );

			// Build O(1) lookup map for content slots by divId.
			var contentSlotMap = {};
			Object.keys( contentSlots ).forEach( function( slotId ) {
				var slotConfig = contentSlots[ slotId ];
				var divId = 'ad-in-' + slotConfig.position;
				contentSlotMap[ divId ] = { slotId: slotId, config: slotConfig };
			} );

			// Query all ad slots in DOM order (same selector as defineAllSlots).
			var allElements = document.querySelectorAll(
				'.pmd-ad-slot[data-slot-id], .pmd-native-ad-slot[data-slot-id]'
			);

			allElements.forEach( function( element ) {
				var divId = element.id;
				if ( ! divId ) {
					return;
				}

				// Skip native slots (fluid, not relevant for header bidding).
				if ( element.classList.contains( 'pmd-native-ad-slot' ) ) {
					return;
				}

				if ( oopSlot && element.hasAttribute( 'data-ad-oop' ) && divId === oopSlot.divId ) {
					slots.push( {
						divId:  oopSlot.divId,
						slotId: oopSlot.divId,
						path:   oopSlot.path,
						sizes:  [ [ 1, 1 ] ]
					} );
					return;
				}

				if ( configSlotMap[ divId ] && configSlotMap[ divId ].position === 'page' ) {
					var slotId = element.getAttribute( 'data-slot-id' );
					if ( slotId ) {
						var sizesFw = self.parseSizesString( element.getAttribute( 'data-sizes-fw' ) );
						var sizesStandard = self.parseSizesString( element.getAttribute( 'data-sizes-standard' ) );
						var sizesMobile = self.parseSizesString( element.getAttribute( 'data-sizes-mobile' ) );

						slots.push( {
							divId:  divId,
							slotId: slotId,
							sizes:  [].concat( sizesFw, sizesStandard, sizesMobile )
						} );
					}
				} else if ( contentSlotMap[ divId ] ) {
					var contentData = contentSlotMap[ divId ];
					slots.push( {
						divId:  divId,
						slotId: contentData.slotId,
						path:   contentData.config.path,
						sizes:  contentData.config.sizes || []
					} );
				} else if ( configSlotMap[ divId ] ) {
					var slotConfig = configSlotMap[ divId ];
					if ( self.hasActiveSizes( slotConfig ) ) {
						slots.push( {
							divId:  divId,
							slotId: slotConfig.slotId || slotConfig.divId,
							path:   slotConfig.path,
							sizes:  slotConfig.sizes || []
						} );
					}
				}
			} );

			return slots;
		},

		/**
		 * Wait for GPT to be available.
		 *
		 * @param {Function} callback Callback to execute when GPT is ready.
		 */
		waitForGPT: function( callback ) {
			if ( typeof googletag !== 'undefined' && googletag.cmd ) {
				callback();
			} else {
				setTimeout( function() {
					this.waitForGPT( callback );
				}.bind( this ), 50 );
			}
		},

		/**
		 * Setup GPT configuration.
		 */
			setupGPT: function() {
				var gamConfig = this.config.gam || {};
				var lazyConfig = this.config.lazyLoad || {};

				googletag.cmd.push( function() {
					var config = {};

					if ( gamConfig.collapseEmpty ) {
						config.collapseEmptyDivs = true;
					}

					if ( gamConfig.sraEnabled ) {
						config.singleRequest = true;
					}

					if ( lazyConfig.enabled ) {
						config.lazyLoad = {
							fetchMarginPercent: lazyConfig.fetchMargin || 50,
							renderMarginPercent: lazyConfig.renderMargin || 25,
							mobileScaling: lazyConfig.mobileScaling || 2.0
						};
					}

					if ( Object.keys( config ).length ) {
						googletag.setConfig( config );
					}
				} );
			},

			/**
			 * Apply a single slot-level targeting key using GPT config APIs.
			 *
			 * @param {Object}               slot  GPT slot object.
			 * @param {string}               key   Targeting key.
			 * @param {string|Array<string>} value Targeting value.
			 */
			setSlotTargeting: function( slot, key, value ) {
				var targeting;

				if ( ! slot || ! key || typeof slot.setConfig !== 'function' ) {
					return;
				}

				targeting = {};
				targeting[ key ] = value;

				slot.setConfig( { targeting: targeting } );
			},

			/**
			 * Read a single slot-level targeting key using GPT config APIs.
			 *
			 * @param {Object} slot GPT slot object.
			 * @param {string} key  Targeting key.
			 * @return {Array<string>} Normalized targeting values.
			 */
			getSlotTargetingValues: function( slot, key ) {
				var config;
				var value;

				if ( ! slot || ! key || typeof slot.getConfig !== 'function' ) {
					return [];
				}

				config = slot.getConfig( 'targeting' );
				if (
					! config ||
					! config.targeting ||
					! Object.prototype.hasOwnProperty.call( config.targeting, key )
				) {
					return [];
				}

				value = config.targeting[ key ];
				if ( null === value || typeof value === 'undefined' ) {
					return [];
				}

				if ( Array.isArray( value ) ) {
					return value.map( function( item ) {
						return String( item );
					} );
				}

				return [ String( value ) ];
			},

			/**
			 * Apply a single page-level targeting key using GPT config APIs.
			 *
			 * @param {string}               key   Targeting key.
			 * @param {string|Array<string>} value Targeting value.
			 */
			setPageTargeting: function( key, value ) {
				var targeting;

				if (
					! key ||
					typeof googletag === 'undefined' ||
					typeof googletag.setConfig !== 'function'
				) {
					return;
				}

				targeting = {};
				targeting[ key ] = value;

				googletag.setConfig( { targeting: targeting } );
			},

			/**
			 * Build a map of div IDs to loc values based on DOM order.
		 * Display and right-rail slots maintain separate counters.
		 * Native slots have an independent counter.
		 */
		buildLocMap: function() {
			var displayCounter = 0;
			var rightRailCounter = 0;
			var nativeCounter = 0;

			// Build a set of right-rail div IDs from config.
			var rightRailDivIds = {};
			var configSlots = this.config.slots || [];
			configSlots.forEach( function( slotConfig ) {
				if ( slotConfig.position === 'right-rail' ) {
					rightRailDivIds[ slotConfig.divId ] = true;
				}
			} );

			// Query all display ad slots in DOM order.
			var displaySlots = document.querySelectorAll( '.pmd-ad-slot[data-slot-id]' );
			displaySlots.forEach( function( element ) {
				var divId = element.id;
				if ( ! divId ) {
					return;
				}
				if ( element.hasAttribute( 'data-ad-oop' ) ) {
					return;
				}

				if ( rightRailDivIds[ divId ] ) {
					rightRailCounter++;
					this.locMap[ divId ] = 'rr' + rightRailCounter;
				} else {
					displayCounter++;
					this.locMap[ divId ] = String( displayCounter );
				}
			}.bind( this ) );

			// Query all native ad slots in DOM order (independent counter).
			var nativeSlots = document.querySelectorAll( '.pmd-native-ad-slot' );
			nativeSlots.forEach( function( element ) {
				var divId = element.id;
				if ( ! divId ) {
					return;
				}

				nativeCounter++;
				this.locMap[ divId ] = String( nativeCounter );
			}.bind( this ) );
		},

		/**
		 * Build a map of div IDs to standardized ad_slot targeting values.
		 *
		 * Each placement gets a unique identifier that combines the placement
		 * role, position variant (DOM order within its category), and size
		 * profile suffix:
		 *
		 *  - top_banner_LDR / bottom_banner_LDR  (header banners)
		 *  - in_feed_{N}_LDR                     (page-injected leaderboards)
		 *  - in_article_{N}_LDR                  (in-article display)
		 *  - right_rail_primary_BOX              (first right rail)
		 *  - right_rail_secondary_SQ             (second right rail)
		 *  - in_feed_{N}_NAT / in_article_{N}_NAT (native, by page context)
		 *
		 * See VIP-340 for the standardized value catalog.
		 */
		buildAdSlotMap: function() {
			var configSlots = this.config.slots || [];
			var headerDivIds = {};
			var rightRailDivIds = {};
			var pageDivIds = {};
			configSlots.forEach( function( slotConfig ) {
				if ( slotConfig.position === 'header' ) {
					headerDivIds[ slotConfig.divId ] = true;
				} else if ( slotConfig.position === 'right-rail' ) {
					rightRailDivIds[ slotConfig.divId ] = true;
				} else if ( slotConfig.position === 'page' ) {
					pageDivIds[ slotConfig.divId ] = true;
				}
			} );

			// First DOM-order pass: classify display slots and capture
			// each header banner so we can resolve top vs bottom in pass 2.
			var headerEntries = [];
			var inFeedLdrCounter = 0;
			var inArticleLdrCounter = 0;
			var rightRailCounter = 0;

			var displaySlots = document.querySelectorAll( '.pmd-ad-slot[data-slot-id]' );
			displaySlots.forEach( function( element ) {
				var divId = element.id;
				if ( ! divId ) {
					return;
				}
				if ( element.hasAttribute( 'data-ad-oop' ) ) {
					return;
				}

				if ( headerDivIds[ divId ] ) {
					headerEntries.push( divId );
				} else if ( rightRailDivIds[ divId ] ) {
					rightRailCounter++;
					if ( rightRailCounter === 1 ) {
						this.adSlotMap[ divId ] = 'right_rail_primary_BOX';
					} else if ( rightRailCounter === 2 ) {
						this.adSlotMap[ divId ] = 'right_rail_secondary_SQ';
					} else {
						// More than two right-rail slots is non-standard;
						// fall back to the secondary value to keep targeting valid.
						this.adSlotMap[ divId ] = 'right_rail_secondary_SQ';
					}
				} else if ( pageDivIds[ divId ] ) {
					inFeedLdrCounter++;
					this.adSlotMap[ divId ] = 'in_feed_' + inFeedLdrCounter + '_LDR';
				} else {
					// In-article display (e.g. ad-in1, ad-in2).
					inArticleLdrCounter++;
					this.adSlotMap[ divId ] = 'in_article_' + inArticleLdrCounter + '_LDR';
				}
			}.bind( this ) );

			// Resolve header banners: first in DOM order = top, last = bottom.
			// When only one banner exists, treat it as the top banner.
			if ( headerEntries.length === 1 ) {
				this.adSlotMap[ headerEntries[ 0 ] ] = 'top_banner_LDR';
			} else if ( headerEntries.length > 1 ) {
				this.adSlotMap[ headerEntries[ 0 ] ] = 'top_banner_LDR';
				for ( var i = 1; i < headerEntries.length; i++ ) {
					this.adSlotMap[ headerEntries[ i ] ] = 'bottom_banner_LDR';
				}
			}

			// Native slots — classify by page context (single/article vs. feed).
			var bodyEl = document.body;
			var isArticleContext = bodyEl
				? ( bodyEl.classList.contains( 'single' ) || bodyEl.classList.contains( 'single-post' ) )
				: false;
			var inFeedNatCounter = 0;
			var inArticleNatCounter = 0;

			var nativeSlots = document.querySelectorAll( '.pmd-native-ad-slot' );
			nativeSlots.forEach( function( element ) {
				var divId = element.id;
				if ( ! divId ) {
					return;
				}

				if ( isArticleContext ) {
					inArticleNatCounter++;
					this.adSlotMap[ divId ] = 'in_article_' + inArticleNatCounter + '_NAT';
				} else {
					inFeedNatCounter++;
					this.adSlotMap[ divId ] = 'in_feed_' + inFeedNatCounter + '_NAT';
				}
			}.bind( this ) );
		},

		/**
		 * Set ad_slot targeting on a GPT slot from the pre-computed map.
		 *
		 * @param {Object} slot  GPT slot object.
		 * @param {string} divId The div element ID used as the lookup key.
		 */
		setAdSlotTargeting: function( slot, divId ) {
			if ( ! slot || ! divId ) {
				return;
			}
			var value = this.adSlotMap[ divId ];
			if ( value ) {
				this.setSlotTargeting( slot, TARGETING_KEY_AD_SLOT, value );
			}
		},

		/**
		 * Check if a config slot has active sizes at the current viewport.
		 *
		 * When a slot's size mapping resolves to an empty array for the
		 * current viewport, the slot should not be defined — defining it
		 * would waste an SRA request slot even though GPT cannot fill it.
		 *
		 * @param {Object} slotConfig Config object from pmdAdsConfig.slots.
		 * @return {boolean} True if the slot has sizes at this viewport.
		 */
		hasActiveSizes: function( slotConfig ) {
			var mapping = slotConfig.sizeMapping;
			if ( ! mapping || ! mapping.length ) {
				return true;
			}

			var viewportWidth = window.innerWidth;

			// sizeMapping is sorted descending by viewport width.
			for ( var i = 0; i < mapping.length; i++ ) {
				if ( viewportWidth >= mapping[ i ].viewport[ 0 ] ) {
					return mapping[ i ].sizes.length > 0;
				}
			}

			return true;
		},

		/**
		 * Detect interscroller `pos` value from sizes.
		 *
		 * @param {Array} sizes Slot sizes.
		 * @return {string} Interscroller `pos` value or empty string.
		 */
		getInterscrollerPosForSizes: function( sizes ) {
			if ( ! Array.isArray( sizes ) || ! sizes.length ) {
				return '';
			}

			var hasSixBySix = false;
			var hasSevenBySeven = false;

			sizes.forEach( function( size ) {
				var width;
				var height;

				if ( Array.isArray( size ) && size.length >= 2 ) {
					width = parseInt( size[ 0 ], 10 );
					height = parseInt( size[ 1 ], 10 );
				} else if ( typeof size === 'string' ) {
					var parts = size.split( 'x' );
					if ( parts.length === 2 ) {
						width = parseInt( parts[ 0 ], 10 );
						height = parseInt( parts[ 1 ], 10 );
					}
				}

				if ( isNaN( width ) || isNaN( height ) ) {
					return;
				}

				if ( width === 6 && height === 6 ) {
					hasSixBySix = true;
				} else if ( width === 7 && height === 7 ) {
					hasSevenBySeven = true;
				}
			} );

			if ( hasSixBySix ) {
				return 'interscroller1';
			}

			if ( hasSevenBySeven ) {
				return 'interscroller2';
			}

			return '';
		},

		/**
		 * Determine and set slot `pos` targeting.
		 *
		 * Priority:
		 * 1) interscroller from active sizes (6x6/7x7)
		 * 2) context pos
		 *
		 * @param {Object} slot GPT slot object.
		 * @param {Array}  actualSizes Active sizes for the current viewport.
		 * @param {string} contextPos Context-based `pos` value.
		 * @return {string} Applied `pos` value or empty string.
		 */
		setPosTargeting: function( slot, actualSizes, contextPos ) {
			if ( ! slot ) {
				return '';
			}

			var pos = this.getInterscrollerPosForSizes( actualSizes || [] );
			if ( ! pos ) {
				pos = contextPos || '';
			}

			if ( pos ) {
				this.setSlotTargeting( slot, 'pos', pos );
			}

			return pos;
		},

		/**
		 * Resolve the slot-type targeting value from DOM metadata.
		 *
		 * @param {Element|null} element The slot DOM element.
		 * @param {string}       fallback Default slot type.
		 * @return {string} Slot type targeting value.
		 */
		getSlotTypeTargeting: function( element, fallback ) {
			if ( element && typeof element.getAttribute === 'function' ) {
				var slotType = element.getAttribute( 'data-slot-type' );
				if ( slotType ) {
					return slotType;
				}
			}

			return fallback || SLOT_TYPE_DISPLAY;
		},

		/**
		 * Set slot-type targeting on a GPT slot.
		 *
		 * @param {Object} slot GPT slot object.
		 * @param {string} slotType Slot type targeting value.
		 */
		setSlotTypeTargeting: function( slot, slotType ) {
			if ( slot && slotType ) {
				this.setSlotTargeting( slot, TARGETING_KEY_SLOT, slotType );
			}
		},

		/**
		 * Read the current URL search string without referencing window.location.
		 *
		 * @return {string} Current URL search string, including the leading '?'.
		 */
		getCurrentUrlSearch: function() {
			if ( ! document.URL || typeof document.URL !== 'string' ) {
				return '';
			}

			try {
				return new URL( document.URL ).search || '';
			} catch ( e ) {
				return '';
			}
		},

		/**
		 * Normalize a URL or hostname into a GAM-safe referring domain value.
		 *
		 * @param {string} value URL or hostname source.
		 * @return {string} Hostname without protocol, path, query, port, or trailing dots.
		 */
		normalizeReferrerDomain: function( value ) {
			if ( ! value || typeof value !== 'string' ) {
				return '';
			}

			var source = value.trim();
			var parseSource = source;
			var url;
			var hostname;

			if ( ! source ) {
				return '';
			}

			if ( parseSource.indexOf( '//' ) === 0 ) {
				parseSource = 'https:' + parseSource;
			} else if ( ! /^[a-z][a-z0-9+.-]*:\/\//i.test( parseSource ) ) {
				parseSource = 'https://' + parseSource;
			}

			try {
				url = new URL( parseSource );
			} catch ( e ) {
				return '';
			}

			hostname = ( url.hostname || '' ).toLowerCase().replace( /\.+$/, '' );

			if ( ! hostname || /[^a-z0-9.-]/.test( hostname ) ) {
				return '';
			}

			return hostname;
		},

		/**
		 * Get the explicit r= tracking value from the current page URL.
		 *
		 * @return {string} Raw r parameter value, or an empty string.
		 */
		getReferrerUrlParam: function() {
			var search = this.getCurrentUrlSearch();
			var params;

			if ( ! search ) {
				return '';
			}

			try {
				params = new URLSearchParams( search );
			} catch ( e ) {
				return '';
			}

			return params.get( TARGETING_KEY_REFERRER ) || '';
		},

		/**
		 * Resolve the referring domain targeting value.
		 *
		 * @return {string} Normalized referring domain, or an empty string.
		 */
		getReferrerTargeting: function() {
			if ( this.referrerTargetingResolved ) {
				return this.referrerTargeting;
			}

			this.referrerTargeting = this.normalizeReferrerDomain( this.getReferrerUrlParam() );

			if ( ! this.referrerTargeting ) {
				this.referrerTargeting = this.normalizeReferrerDomain( document.referrer || '' );
			}

			this.referrerTargetingResolved = true;

			return this.referrerTargeting;
		},

		/**
		 * Apply referring domain targeting at the page level when available.
		 */
		applyReferrerTargeting: function() {
			var referrer = this.getReferrerTargeting();

			if ( referrer ) {
				this.setPageTargeting( TARGETING_KEY_REFERRER, referrer );
			}
		},

		/**
		 * Get supported UTM targeting from the current URL.
		 *
		 * Values are read from the raw query string so the exact URL value is
		 * preserved and only non-empty supported keys are applied.
		 *
		 * @return {Object} Map of supported UTM keys to query-string values.
		 */
		getUtmTargeting: function() {
			if ( this.utmTargeting ) {
				return this.utmTargeting;
			}

			var targeting = {};
			var search = this.getCurrentUrlSearch();

			if ( ! search ) {
				this.utmTargeting = targeting;
				return targeting;
			}

			search.replace( /^\?/, '' ).split( '&' ).forEach( function( pair ) {
				if ( ! pair ) {
					return;
				}

				var separatorIndex = pair.indexOf( '=' );
				var key = separatorIndex === -1 ? pair : pair.slice( 0, separatorIndex );
				if ( UTM_TARGETING_KEYS.indexOf( key ) === -1 ) {
					return;
				}

				if ( Object.prototype.hasOwnProperty.call( targeting, key ) ) {
					return;
				}

				var value = separatorIndex === -1 ? '' : pair.slice( separatorIndex + 1 );
				if ( value ) {
					targeting[ key ] = value;
				}
			} );

			this.utmTargeting = targeting;

			return targeting;
		},

			/**
			 * Apply supported URL UTM targeting at the page level.
			 */
			applyUtmTargeting: function() {
				var targeting = this.getUtmTargeting();
				Object.keys( targeting ).forEach( function( key ) {
					this.setPageTargeting( key, targeting[ key ] );
				}.bind( this ) );
			},


		/**
		 * Resolve the ticket-defined fingerprint page type.
		 *
		 * @return {string} hp, sp, or sf.
		 */
		getFingerprintPageType: function() {
			var pageType = this.config && this.config.fingerprintPageType;

			if ( pageType === 'hp' || pageType === 'sp' || pageType === 'sf' ) {
				return pageType;
			}

			return 'sf';
		},

		/**
		 * Resolve the minimum desktop breakpoint used for fingerprint platform detection.
		 *
		 * @return {number}
		 */
		getFingerprintMobileBreakpoint: function() {
			var breakpoint = Number(
				( this.config && this.config.fingerprintMobileBreakpoint ) ||
				( window.pmdAdsConfig && window.pmdAdsConfig.fingerprintMobileBreakpoint )
			);

			if ( Number.isFinite( breakpoint ) && breakpoint > 0 ) {
				return breakpoint;
			}

			return null;
		},

		/**
		 * Resolve the current viewport tier client-side.
		 *
		 * Native ad slots use 'fluid' sizes with no sizeMapping, so the `layout`
		 * KV alone drives creative selection. The page-level `layout` value comes
		 * from the pmd_vp cookie and can be stale (e.g. devtools/panel narrowed
		 * the viewport on a prior request), causing tablet creatives to be served
		 * on desktop. Reading the value set by the inline head script gives us
		 * the real measured tier at page-load time.
		 *
		 * @return {string} mobile | tablet | desktop.
		 */
		getCurrentLayoutTier: function() {
			var html = document.documentElement;
			var attrValue = html && typeof html.getAttribute === 'function' ? html.getAttribute( 'data-vp' ) : '';

			if ( attrValue === LAYOUT_TIER_MOBILE || attrValue === LAYOUT_TIER_TABLET || attrValue === LAYOUT_TIER_DESKTOP ) {
				return attrValue;
			}

			var width = window.innerWidth || ( html && html.clientWidth ) || 0;

			if ( width < LAYOUT_BP_TABLET ) {
				return LAYOUT_TIER_MOBILE;
			}

			if ( width < LAYOUT_BP_DESKTOP ) {
				return LAYOUT_TIER_TABLET;
			}

			return LAYOUT_TIER_DESKTOP;
		},

		/**
		 * Map a pmd-paywall state string to the obfuscated `ut` KV value. Values are
		 * obfuscated (1/2/3) so no PII is sent to GAM. Anything other than the known
		 * registered/subscribed states (including undefined when the paywall isn't
		 * active) maps to anonymous — no session === anonymous visitor.
		 *
		 * @param {string} state window.pmdNaviga.state value.
		 * @return {string} '1' anonymous | '2' registered | '3' subscriber.
		 */
		mapPaywallStateToUserType: function( state ) {
			if ( state === PAYWALL_STATE_SUBSCRIBED ) {
				return USER_TYPE_SUBSCRIBER;
			}

			if ( state === PAYWALL_STATE_REGISTERED ) {
				return USER_TYPE_REGISTERED;
			}

			return USER_TYPE_ANONYMOUS;
		},

		/**
		 * Best-effort synchronous read of the authenticated user tier from the
		 * paywall's published state. Returns whatever has resolved so far, without
		 * waiting for Engage's terminal decision — use applyUserTypeTargeting for
		 * the accurate ad-request value.
		 *
		 * @return {string} '1' anonymous | '2' registered | '3' subscriber.
		 */
		getUserType: function() {
			const naviga = window.pmdNaviga || {};
			return this.mapPaywallStateToUserType( naviga.state );
		},

		/**
		 * Keep `ut` current for ad refreshes after the initial targeting pass.
		 *
		 * The first ad call waits for a confirmed tier. If Engage confirms after
		 * that bounded wait, this listener updates targeting for later GAM requests.
		 */
		bindUserTypeTargetingUpdates: function() {
			document.addEventListener( PAYWALL_STATE_CHANGE_EVENT, function( event ) {
				const detail = event && event.detail ? event.detail : {};

				if ( true !== detail.userAccessLevelConfirmed ) {
					return;
				}

				this.applyConfirmedUserTypeTargeting( detail.state );
			}.bind( this ) );
		},

		/**
		 * Apply a confirmed Naviga user state to GPT page-level targeting.
		 *
		 * @param {string} state Confirmed window.pmdNaviga.state value.
		 */
		applyConfirmedUserTypeTargeting: function( state ) {
			const self = this;

			if (
				typeof googletag === 'undefined' ||
				! googletag.cmd ||
				typeof googletag.cmd.push !== 'function'
			) {
				return;
			}

			googletag.cmd.push( function() {
				self.setPageTargeting( TARGETING_KEY_USER_TYPE, self.mapPaywallStateToUserType( state ) );
			} );
		},

		/**
		 * Resolve and apply the `ut` (User Type) page-level KV, waiting for the
		 * paywall's terminal decision when necessary.
		 *
		 * pmd-paywall (Naviga Engage) decides the tier client-side and publishes it
		 * on window.pmdNaviga.state, setting window.pmdNaviga.userAccessLevelConfirmed
		 * true once Engage has reached its terminal account-status decision. The
		 * subscriber promotion (registered → subscribed) is asynchronous and routinely
		 * lands AFTER ads would otherwise call, so reading the state synchronously
		 * mislabels subscribers as registered (`ut=2` instead of `ut=3`, VIP-351).
		 *
		 * Resolution, ordered so only logged-in users awaiting Engage ever wait:
		 *  - Anonymous (no session) can never upgrade → resolve immediately.
		 *  - Already-confirmed state (subscribed, or registered confirmed) → resolve now.
		 *  - Registered but not yet confirmed → wait (bounded) for the terminal
		 *    pmd:naviga-state-change event. On timeout we re-read window.pmdNaviga
		 *    and only assert `ut` if Engage has by then CONFIRMED the tier; a still-
		 *    unconfirmed registered is just the cookie guess, so we omit `ut` rather
		 *    than risk mislabelling a slow-to-resolve subscriber as ut=2 (VIP-351).
		 *
		 * @param {Function} done Callback once the `ut` KV has been applied.
		 */
		applyUserTypeTargeting: function( done ) {
			const self = this;
			const naviga = window.pmdNaviga || {};
			const state = naviga.state;

			function apply( resolvedState ) {
				self.setPageTargeting( TARGETING_KEY_USER_TYPE, self.mapPaywallStateToUserType( resolvedState ) );
				done();
			}

			// Fast paths — no wait: non-"registered" states can't change to a
			// different tier here (anonymous can't upgrade, subscribed is terminal),
			// and a registered state Engage has already confirmed is done.
			if ( state !== PAYWALL_STATE_REGISTERED || true === naviga.userAccessLevelConfirmed ) {
				apply( state );
				return;
			}

			// Registered guess/interim: Engage may still promote to subscribed.
			// Wait bounded for its terminal decision.
			let settled = false;
			let timer = null;

			function finish() {
				if ( settled ) {
					return;
				}
				settled = true;
				if ( timer ) {
					window.clearTimeout( timer );
				}
				document.removeEventListener( PAYWALL_STATE_CHANGE_EVENT, onStateChange );
				// Re-read live state: the terminal event may have fired before this
				// listener attached. Only assert `ut` once Engage has CONFIRMED the
				// tier — a still-unconfirmed `registered` is just the pre-paint cookie
				// guess, which cannot tell a subscriber from a registered user. On a
				// slow/timed-out resolve, emitting ut=2 would mislabel a subscriber
				// (VIP-351), so omit the KV entirely instead of sending a wrong value.
				const current = window.pmdNaviga || {};
				if ( true === current.userAccessLevelConfirmed ) {
					apply( current.state );
				} else {
					done();
				}
			}

			function onStateChange( event ) {
				if ( event && event.detail && event.detail.userAccessLevelConfirmed ) {
					finish();
				}
			}

			document.addEventListener( PAYWALL_STATE_CHANGE_EVENT, onStateChange );
			timer = window.setTimeout( finish, USER_TYPE_RESOLVE_TIMEOUT_MS );
		},

		/**
		 * Resolve the ticket-defined fingerprint platform.
		 *
		 * @return {string} mw or dtw.
		 */
		getFingerprintPlatform: function() {
			var breakpoint = this.getFingerprintMobileBreakpoint();

			if ( ! breakpoint ) {
				return 'dtw';
			}

			if ( window.matchMedia && window.matchMedia( '(max-width: ' + String( breakpoint - 1 ) + 'px)' ).matches ) {
				return 'mw';
			}

			return window.innerWidth < breakpoint ? 'mw' : 'dtw';
		},

		/**
		 * Convert slot sizes into the ticket-defined slot_array string.
		 *
		 * @param {Array|string} sizes Slot sizes or the string 'fluid'.
		 * @return {string}
		 */
		formatFingerprintSlotArray: function( sizes ) {
			if ( sizes === 'fluid' ) {
				return 'fluid';
			}

			if ( typeof sizes === 'string' ) {
				return sizes;
			}

			if ( ! Array.isArray( sizes ) || ! sizes.length ) {
				return '';
			}

			var seen = {};
			var sizeStrings = [];

			sizes.forEach( function( size ) {
				var sizeString = '';

				if ( Array.isArray( size ) && size.length >= 2 ) {
					sizeString = String( size[0] ) + 'x' + String( size[1] );
				} else if ( size && typeof size.getWidth === 'function' && typeof size.getHeight === 'function' ) {
					sizeString = String( size.getWidth() ) + 'x' + String( size.getHeight() );
				} else if ( typeof size === 'string' ) {
					sizeString = size;
				}

				if ( sizeString && ! seen[ sizeString ] ) {
					seen[ sizeString ] = true;
					sizeStrings.push( sizeString );
				}
			} );

			return sizeStrings.join( '|' );
		},

		/**
		 * Build normalized fingerprint data from slot context.
		 *
		 * @param {Object} options Fingerprint context.
		 * @return {Object}
		 */
		getFingerprintData: function( options ) {
			var refreshCount = Number( options && options.refreshCount );

			return {
				platform:     options && options.platform ? options.platform : this.getFingerprintPlatform(),
				pageType:     options && options.pageType ? options.pageType : this.getFingerprintPageType(),
				mediaType:    options && options.mediaType ? options.mediaType : SLOT_TYPE_DISPLAY,
				adSlot:       options && options.adSlot ? options.adSlot : '',
				location:     String( options && undefined !== options.location ? options.location : '' ),
				slotArray:    this.formatFingerprintSlotArray( options && undefined !== options.slotArray ? options.slotArray : [] ),
				refreshCount: Number.isFinite( refreshCount ) ? refreshCount : 0,
			};
		},

		/**
		 * Build fingerprint data for a display slot.
		 *
		 * @param {string}       location      Slot loc targeting value.
		 * @param {string}       position      Slot position metadata.
		 * @param {Array|string} slotArray     Slot sizes.
		 * @param {Element|null} element       Slot DOM element.
		 * @param {number}       refreshCount  Refresh count integer.
		 * @param {string|null}  adSlot        Explicit ad_slot override.
		 * @return {Object}
		 */
		getDisplayFingerprintData: function( location, position, slotArray, element, refreshCount, adSlot ) {
			var pageType = this.getFingerprintPageType();

			return this.getFingerprintData( {
				pageType:     pageType,
				mediaType:    SLOT_TYPE_DISPLAY,
				adSlot:       adSlot || this.getDisplayFingerprintAdSlot( location, position, pageType, element ),
				location:     location,
				slotArray:    slotArray,
				refreshCount: refreshCount,
			} );
		},

		/**
		 * Build fingerprint data for a native slot.
		 *
		 * @param {string}       location      Slot loc targeting value.
		 * @param {Element|null} element       Native slot DOM element.
		 * @param {number}       refreshCount  Refresh count integer.
		 * @return {Object}
		 */
		getNativeFingerprintData: function( location, element, refreshCount ) {
			return this.getFingerprintData( {
				mediaType:    'native',
				adSlot:       this.getNativeFingerprintAdSlot( element ),
				location:     location,
				slotArray:    'fluid',
				refreshCount: refreshCount,
			} );
		},

		/**
		 * Build fingerprint data for a video request.
		 *
		 * @param {string} location      Slot loc targeting value.
		 * @param {string} adSlot        Video ad_slot value.
		 * @param {string} slotArray     Video slot_array value.
		 * @param {number} refreshCount  Refresh count integer.
		 * @return {Object}
		 */
		getVideoFingerprintData: function( location, adSlot, slotArray, refreshCount ) {
			return this.getFingerprintData( {
				mediaType:    'video',
				adSlot:       adSlot,
				location:     location,
				slotArray:    slotArray,
				refreshCount: refreshCount,
			} );
		},

		/**
		 * Resolve the fingerprint slot_array sizes for the active viewport.
		 *
		 * @param {Array} fallbackSizes Default slot sizes.
		 * @param {Array} sizeMapping   Viewport-to-size mapping.
		 * @return {Array}
		 */
		getActiveDisplayFingerprintSizes: function( fallbackSizes, sizeMapping ) {
			if ( Array.isArray( sizeMapping ) && sizeMapping.length ) {
				var mappedSizes = this.getSizesFromMapping( sizeMapping );
				if ( Array.isArray( mappedSizes ) && mappedSizes.length ) {
					return mappedSizes;
				}
			}

			return Array.isArray( fallbackSizes ) ? fallbackSizes : [];
		},

		/**
		 * Determine the ticket-defined display ad_slot value.
		 *
		 * @param {string} location   Slot loc targeting value.
		 * @param {string} position   Slot position metadata.
		 * @param {string} pageType   Fingerprint page type.
		 * @param {Element|null} el   Slot DOM element.
		 * @return {string}
		 */
		getDisplayFingerprintAdSlot: function( location, position, pageType, el ) {
			if ( el && el.hasAttribute( 'data-ad-oop' ) ) {
				return 'topbanner';
			}

			if ( position === 'header' ) {
				if ( el && el.id && this.adSlotMap[ el.id ] === 'bottom_banner_LDR' ) {
					return 'bottombanner';
				}

				return 'topbanner';
			}

			if ( String( location ).indexOf( 'rr' ) === 0 || position === 'right-rail' ) {
				return pageType === 'sp' ? 'inarticlerr' : 'infeedfw';
			}

			return pageType === 'sp' ? 'inarticle' : 'infeedfw';
		},

		/**
		 * Determine the ticket-defined native ad_slot value.
		 *
		 * @param {Element|null} element Native slot element.
		 * @return {string}
		 */
		getNativeFingerprintAdSlot: function( element ) {
			if ( element && typeof element.getAttribute === 'function' ) {
				var pos = element.getAttribute( 'data-pos' );
				if ( pos ) {
					return pos;
				}
			}

			return 'infeedfw';
		},

		/**
		 * Build an ad context fingerprint string.
		 *
		 * @param {Object} data Fingerprint segments.
		 * @return {string}
		 */
		buildAdFingerprint: function( data ) {
			return [
				data.platform,
				data.pageType,
				data.mediaType,
				data.adSlot,
				data.location,
				data.slotArray,
				'rc' + String( data.refreshCount )
			].join( '_' );
		},

		/**
		 * Set ad_cfp targeting on a slot.
		 *
		 * @param {Object} slot GPT slot.
		 * @param {Object} data Fingerprint segments.
		 */
		setAdFingerprint: function( slot, data ) {
			if ( slot ) {
				this.setSlotTargeting( slot, TARGETING_KEY_AD_FINGERPRINT, this.buildAdFingerprint( data ) );
			}
		},

		/**
		 * Update the refresh-count segment of an existing ad fingerprint.
		 *
		 * @param {Object} slot         GPT slot.
		 * @param {number} refreshCount Refresh count integer.
		 */
		updateAdFingerprintRefreshCount: function( slot, refreshCount ) {
			if ( ! slot ) {
				return;
			}

			var fingerprintArray = this.getSlotTargetingValues( slot, TARGETING_KEY_AD_FINGERPRINT );
			if ( ! Array.isArray( fingerprintArray ) || ! fingerprintArray.length || ! fingerprintArray[0] ) {
				return;
			}

			var parts = String( fingerprintArray[0] ).split( '_' );
			if ( parts.length !== 7 ) {
				return;
			}

			parts[6] = 'rc' + String( refreshCount );
			this.setSlotTargeting( slot, TARGETING_KEY_AD_FINGERPRINT, parts.join( '_' ) );
		},

		/**
		 * Define the out-of-page (1x1) slot.
		 *
		 * @param {Object} oopSlot OOP slot configuration.
		 */
		defineOopSlot: function( oopSlot ) {
			if ( ! oopSlot || ! oopSlot.path || ! oopSlot.divId ) {
				return;
			}

			var element = document.getElementById( oopSlot.divId );
			var oop = googletag.defineOutOfPageSlot( oopSlot.path, oopSlot.divId );
			if ( ! oop ) {
				return;
			}

				if ( oopSlot.customTargeting ) {
					Object.keys( oopSlot.customTargeting ).forEach( function( key ) {
						if ( PAGE_LEVEL_ONLY_TARGETING_KEYS[ String( key ).toLowerCase() ] ) {
							return;
						}
						this.setSlotTargeting( oop, key, oopSlot.customTargeting[ key ] );
					}.bind( this ) );
				}

				this.setSlotTypeTargeting(
					oop,
					this.getSlotTypeTargeting( element, SLOT_TYPE_DISPLAY )
				);
				this.setSlotTargeting( oop, 'loc', 'oop' );
				this.setSlotTargeting( oop, TARGETING_KEY_REFRESH, 'false' );
				this.setAdFingerprint( oop, this.getDisplayFingerprintData( 'oop', 'header', [ [ 1, 1 ] ], element, 0, 'infeedfw' ) );

			oop.addService( googletag.pubads() );
			this.definedSlots.push( oop );
		},

		/**
		 * Define all ad slots in a single GPT command, in DOM order.
		 *
		 * Walks the DOM once to discover every ad slot, classifies each into
		 * config / content / page-injected / native, and calls the appropriate
		 * helper. Because all defineSlot() calls happen inside one
		 * googletag.cmd.push(), the SRA request preserves DOM order.
		 */
		defineAllSlots: function() {
			var self = this;
			var configSlots = this.config.slots || [];
			var contentSlots = this.contentSlots || {};
			var nativeConfig = this.config.nativeAds || {};
			var oopSlot = this.config.oopSlot || null;

			// Build O(1) lookup map for config slots by divId.
			var configSlotMap = {};
			configSlots.forEach( function( slotConfig ) {
				if ( slotConfig.divId ) {
					configSlotMap[ slotConfig.divId ] = slotConfig;
				}
			} );

			// Build O(1) lookup map for content slots by divId.
			var contentSlotMap = {};
			Object.keys( contentSlots ).forEach( function( slotId ) {
				var slotConfig = contentSlots[ slotId ];
				var divId = 'ad-in-' + slotConfig.position;
				contentSlotMap[ divId ] = { slotId: slotId, config: slotConfig };
			} );

			// Query ALL ad slots in DOM order.
			var allElements = document.querySelectorAll(
				'.pmd-ad-slot[data-slot-id], .pmd-native-ad-slot[data-slot-id]'
			);

			googletag.cmd.push( function() {
				if ( oopSlot ) {
					self.defineOopSlot( oopSlot );
				}

				allElements.forEach( function( element ) {
					var divId = element.id;
					if ( ! divId ) {
						return;
					}
					if ( element.hasAttribute( 'data-ad-oop' ) ) {
						return;
					}

					// Classify and define each slot.
					if ( element.classList.contains( 'pmd-native-ad-slot' ) ) {
						if ( nativeConfig.enabled ) {
							self.defineNativeSlot( element );
						}
					} else if ( configSlotMap[ divId ] && configSlotMap[ divId ].position === 'page' ) {
						self.definePageSlot( element );
					} else if ( contentSlotMap[ divId ] ) {
						var contentData = contentSlotMap[ divId ];
						self.defineContentSlot( element, contentData.slotId, contentData.config );
					} else if ( configSlotMap[ divId ] ) {
						if ( self.hasActiveSizes( configSlotMap[ divId ] ) ) {
							self.defineConfigSlot( element, configSlotMap[ divId ] );
						}
					}
				} );

			} );
		},

		/**
		 * Define a single config-driven slot (leaderboard, right-rail, etc.).
		 * Must be called inside a googletag.cmd.push() callback.
		 *
		 * @param {Element} element The slot DOM element.
		 * @param {Object}  slotConfig Config object from pmdAdsConfig.slots.
		 */
			defineConfigSlot: function( element, slotConfig ) {
				var slot = googletag.defineSlot(
					slotConfig.path,
					slotConfig.sizes,
				slotConfig.divId
			);

			if ( ! slot ) {
				return;
			}

			// Add size mapping if configured.
			if ( slotConfig.sizeMapping && slotConfig.sizeMapping.length ) {
				var mapping = googletag.sizeMapping();
				slotConfig.sizeMapping.forEach( function( map ) {
					mapping.addSize( map.viewport, map.sizes );
				} );
				slot.defineSizeMapping( mapping.build() );
			}

			// Set pos targeting from active interscroller size.
			var activeConfigSizes = this.getActiveDisplayFingerprintSizes(
				slotConfig.sizes || [],
				slotConfig.sizeMapping || []
			);
				this.setPosTargeting(
					slot,
					activeConfigSizes,
					''
				);

				// Add custom targeting.
				if ( slotConfig.customTargeting ) {
					Object.keys( slotConfig.customTargeting ).forEach( function( key ) {
						if ( PAGE_LEVEL_ONLY_TARGETING_KEYS[ String( key ).toLowerCase() ] ) {
							return;
						}
						this.setSlotTargeting( slot, key, slotConfig.customTargeting[ key ] );
					}.bind( this ) );
				}

				this.setSlotTypeTargeting(
					slot,
					this.getSlotTypeTargeting( element, SLOT_TYPE_DISPLAY )
				);

				// Set loc from pre-computed DOM-order map.
				var loc = this.locMap[ slotConfig.divId ] || slotConfig.divId;
				this.setSlotTargeting( slot, 'loc', loc );

				// Set standardized ad_slot targeting (VIP-340).
				this.setAdSlotTargeting( slot, slotConfig.divId );

				// Add refresh targeting.
				var refreshEnabled = this.config.adRefreshEnabled ? 'true' : 'false';
				this.setSlotTargeting( slot, TARGETING_KEY_REFRESH, refreshEnabled );
				this.setSlotTargeting( slot, 'rc', '0' );
				this.setAdFingerprint( slot, this.getDisplayFingerprintData( loc, slotConfig.position || '', activeConfigSizes, element, 0 ) );
				this.setSlotRefreshInterval( slotConfig.divId, slotConfig.refreshInterval );

				slot.addService( googletag.pubads() );
				this.definedSlots.push( slot );
			},

		/**
		 * Apply page-level targeting, then call back when done.
		 *
		 * @param {Function} callback Fires after all targeting is resolved.
		 */
		applyTargeting: function( callback ) {
			var self = this;
			var targeting = this.config.targeting || {};

			// `targeting.layout` is derived server-side from the pmd_vp cookie and can be
			// stale (CDN cache key, first-visit UA fallback, devtools resize, sessionStorage
			// reload guard exhausted). Override with the actual measured tier before the
			// first ad call so the page-level `layout` KV matches the current viewport.
			targeting[ TARGETING_KEY_LAYOUT ] = this.getCurrentLayoutTier();

			// Session page views — bucketed engagement KV (VIP-353). Incrementing
			// here means the counter advances once per page load that initialises
			// ads, matching the page-level nature of the `spv` targeting value.
			targeting[ TARGETING_KEY_SESSION_PAGE_VIEWS ] = this.getSessionPageViewBucket();

			googletag.cmd.push( function() {
				var pubads = googletag.pubads();
				Object.keys( targeting ).forEach( function( key ) {
					self.setPageTargeting( key, targeting[ key ] );
				} );
				self.applyUtmTargeting();
				self.applyReferrerTargeting();

				var pending = 0;
				var settled = false;

				function resolve() {
					pending--;
					if ( pending <= 0 && ! settled ) {
						settled = true;
						if ( callback ) {
							callback();
						}
					}
				}

				// User type (`ut`) — page-level KV resolved client-side because pages
				// are CDN-cached (VIP-351). Waits for the paywall's terminal decision
				// so subscribers are labelled ut=3, not ut=2.
				pending++;
				self.applyUserTypeTargeting( resolve );

				// Amplitude device ID.
				if ( self.config.enableAmplitudeDevice ) {
					pending++;
					self.applyAmplitudeTargeting( pubads, resolve );
				}

				// Nothing async — fire immediately.
				if ( pending === 0 ) {
					if ( callback ) {
						callback();
					}
				}
			} );
		},

		/**
		 * Increment the cross-tab session page-view counter and return its bucket.
		 *
		 * The count is persisted in localStorage — shared across every tab of the
		 * same origin — as JSON `{ sessionId, count, ts }`, and anchored to the
		 * Amplitude analytics session so `spv` aligns with how sessions are
		 * reported in analytics (VIP-353):
		 *  - Same session — while Amplitude reports the same session id, the count
		 *    accumulates across every open tab.
		 *  - New session — when Amplitude rolls to a new session id (its 30-minute
		 *    inactivity timeout, browser reopen, etc.) the count resets to 1.
		 *  - SDK not ready / analytics disabled — the ad stack initialises before
		 *    the Amplitude SDK on cold loads, so getAmplitudeSessionId() can be
		 *    null. In that window the count is bounded by a 30-minute inactivity
		 *    gap (SPV_SESSION_TIMEOUT_MS) and self-heals onto the real session id
		 *    on the next load once the SDK is available.
		 *
		 * Counts are bucketed into standardized engagement ranges:
		 *  - low   : 1–2 page views
		 *  - med   : 3–5 page views
		 *  - high  : 6–9 page views
		 *  - vhigh : 10+ page views
		 *
		 * @return {string} Bucketed value: 'low', 'med', 'high', or 'vhigh'.
		 */
		getSessionPageViewBucket: function() {
			const now       = Date.now();
			const sessionId = this.getAmplitudeSessionId(); // number, or null if the SDK isn't ready.
			let count       = 0;
			let storedId    = sessionId;

			try {
				const raw = localStorage.getItem( SPV_STORAGE_KEY );
				if ( raw ) {
					const stored = JSON.parse( raw );
					if ( stored && typeof stored.count === 'number' ) {
						const storedHasId = typeof stored.sessionId === 'number';
						const withinWindow =
							typeof stored.ts === 'number' &&
							( now - stored.ts ) < SPV_SESSION_TIMEOUT_MS;

						if ( sessionId !== null && storedHasId && stored.sessionId === sessionId ) {
							// Same Amplitude session — keep counting across tabs.
							count = stored.count;
						} else if ( sessionId !== null && ! storedHasId && withinWindow ) {
							// Earlier loads this session ran before the SDK was ready;
							// adopt that running count now the real id is known.
							count = stored.count;
						} else if ( sessionId === null && withinWindow ) {
							// SDK still not ready — carry within the inactivity window
							// and preserve the last known session id.
							count    = stored.count;
							storedId = storedHasId ? stored.sessionId : null;
						}
						// Otherwise the session changed (or the window lapsed) — reset.
					}
				}
			} catch ( e ) {
				// localStorage unavailable or malformed JSON — start from zero.
			}

			count++;

			try {
				localStorage.setItem(
					SPV_STORAGE_KEY,
					JSON.stringify( { sessionId: storedId, count: count, ts: now } )
				);
			} catch ( e ) {
				// localStorage unavailable — the KV still reflects this page view.
			}

			if ( count <= 2 ) {
				return 'low';
			}
			if ( count <= 5 ) {
				return 'med';
			}
			if ( count <= 9 ) {
				return 'high';
			}
			return 'vhigh';
		},

		/**
		 * Resolve the current Amplitude session id.
		 *
		 * Mirrors getAmplitudeDeviceId(): the Amplitude SDK is loaded by
		 * pmd-analytics.js independently of the ad stack, so on a cold load the ad
		 * code can run before getSessionId() is callable. Returns null in that
		 * case; getSessionPageViewBucket() falls back to a time-based window until
		 * the SDK is ready. Amplitude keeps the session id in sync across tabs, so
		 * using it as the counter key yields a shared cross-tab count.
		 *
		 * @return {number|null} The Amplitude session id, or null if unavailable.
		 */
		getAmplitudeSessionId: function() {
			if (
				typeof window.amplitude !== 'undefined' &&
				typeof window.amplitude.getSessionId === 'function'
			) {
				try {
					const sid = window.amplitude.getSessionId();
					return typeof sid === 'number' ? sid : null;
				} catch ( e ) {
					// SDK present but not fully initialised.
				}
			}
			return null;
		},

		/**
		 * Apply Amplitude device ID as a publisher-provided identifier.
		 *
		 * On subsequent visits the device ID is available instantly from localStorage
		 * (via getAmplitudeDeviceId), so PPID is set synchronously.
		 *
		 * On the very first visit there is no cached ID yet.  Rather than blocking
		 * the ad call for up to 2 s, done() is called immediately so ads are not
		 * delayed.  The SDK initialises in the background and saves the ID to
		 * localStorage; it will be available from the next page load onward.
		 *
		 * @param {Object}   pubads The googletag.pubads() instance.
		 * @param {Function} done   Callback when resolved or timed out.
		 */
		applyAmplitudeTargeting: function( pubads, done ) {
			var ampDeviceId = this.getAmplitudeDeviceId();

			if ( ampDeviceId && pubads && typeof pubads.setPublisherProvidedId === 'function' ) {
				pubads.setPublisherProvidedId( ampDeviceId );
			}

			// Always call done() immediately — cached ID is synchronous, and on first
			// visit we deliberately do not wait so the ad call is never delayed.
			done();
		},

		/**
		 * Get Amplitude device ID.
		 *
		 * Checks sources in priority order:
		 *  1. window.pmdAmplitudeDeviceId — set by the <head> inline script from
		 *     localStorage on page load, or updated by pmd-analytics.js after init.
		 *  2. localStorage — direct fallback in case the window variable is missing.
		 *  3. Live Amplitude SDK — used when the SDK has initialised this session.
		 *
		 * @return {string|null} The device ID or null if not available.
		 */
		getAmplitudeDeviceId: function() {
			// 1. In-memory value set by head script (from localStorage) or analytics init.
			if ( window.pmdAmplitudeDeviceId ) {
				return window.pmdAmplitudeDeviceId;
			}

			// 2. Read directly from localStorage (guards against the window var being
			//    cleared or the head script running after this function is first called).
			try {
				var cached = localStorage.getItem( 'pmd_amplitude_device_id' );
				if ( cached ) {
					window.pmdAmplitudeDeviceId = cached;
					return cached;
				}
			} catch ( e ) {
				// localStorage unavailable — fall through to live SDK check.
			}

			// 3. Live SDK (available once pmd-analytics.js has initialised Amplitude).
			if ( typeof window.amplitude !== 'undefined' && window.amplitude.getDeviceId ) {
				try {
					return window.amplitude.getDeviceId() || null;
				} catch ( e ) {
					// Amplitude not fully initialized.
					return null;
				}
			}
			return null;
		},

		/**
		 * Initialize video ad scheduling.
		 *
		 * Sets the global IMA PPID via google.ima.settings and initialises JW Player
		 * native advertising event bindings. Runs independently of page ad targeting
		 * so that any errors here cannot break display ad calls.
		 */
		initVideoAdScheduling: function() {
			var self = this;

			// Delegate to the video ad events module if available.
			if ( ! window.pmdAds || ! window.pmdAds.videoAdEvents ) {
				return;
			}

			if ( ! self.config.enableAmplitudeDevice ) {
				// No PPID — initialise players without it.
				window.pmdAds.videoAdEvents.initAll();
				return;
			}

			// Resolve the Amplitude device ID (instant on return visits via localStorage).
			// Initialise without ppid on first visit so video ads are never delayed.
			var deviceId = self.getAmplitudeDeviceId();

			/**
			 * Set PPID globally on the IMA SDK settings object.
			 * JW Player's built-in googima client uses google.ima internally
			 * and will automatically include this in all ad requests.
			 */
			window.pmdAds.videoAdEvents.setPpid( deviceId || null );

			/**
			 * Bind JW native advertising events on all registered players.
			 */
			window.pmdAds.videoAdEvents.initAll();
		},

		/**
		 * Legacy video ad initializer.
		 * Kept as a no-op for backwards compatibility.
		 *
		 * @param {string|null} ppid Amplitude device ID (unused).
		 */
		initVideoAds: function( ppid ) { // eslint-disable-line no-unused-vars
			// Video ads are now managed by JW Player native advertising.
		},

		/**
		 * Enable GPT services.
		 */
		enableServices: function() {
			googletag.cmd.push( function() {
				googletag.enableServices();
			} );
		},

		/**
		 * Track rendered slots for refresh.
		 * Only rendered slots should be refreshed to avoid conflicts with lazy loading.
		 */
		trackRenderedSlots: function() {
			var self = this;

			googletag.cmd.push( function() {
				googletag.pubads().addEventListener( 'slotRenderEnded', function( event ) {
					var slot = event.slot;

					// Add to rendered slots if not already tracked.
					if ( self.renderedSlots.indexOf( slot ) === -1 ) {
						self.renderedSlots.push( slot );
					}

					var divId = slot.getSlotElementId();
					var slotElement = document.getElementById( divId );

					// Detect whether this render delivered an interscroller
					// creative (6x6/7x7). Interscroller creatives are pinned to
					// the viewport, so refreshing the slot underneath one causes
					// the layout shift this feature is meant to prevent.
					var renderedSize = event.isEmpty ? null : event.size;
					var isInterscroller =
						!! renderedSize &&
						self.getInterscrollerPosForSizes( [ renderedSize ] ) !== '';

					// Once an interscroller creative loads, pause refresh for the
					// slot for the remainder of the page view (mirrors Cheetah's
					// `data-ad-ref="off"` behaviour). Runs before initSlotRefresh
					// so a first-render interscroller never arms a timer, and
					// stops any timer a prior standard creative already armed.
					if ( isInterscroller ) {
						self.blockRefreshForInterscroller( divId );
					}

					self.initSlotRefresh( slot );

					// Handle native ad empty/fill state.
					if ( slotElement && slotElement.classList.contains( 'pmd-native-ad-slot' ) ) {
						if ( event.isEmpty ) {
							// Collapse entire native container when no native demand fills.
							var container = slotElement.closest( '.pm-ad--native' );
							if ( container ) {
								container.classList.add( 'pm-ad--native-empty' );
							}
						} else {
							slotElement.classList.add( 'pmd-native-ad-loaded' );
						}
					}

					// Reflect the rendered creative on the in-content full-width slot so
					// its background matches: an interscroller (6x6/7x7 trigger) stays
					// transparent (the v2 layout rule) to reveal its fixed creative, while
					// a standard billboard (e.g. 970x250) keeps the grey placeholder
					// background used by in-content ads on non-v2 pages. The colours live
					// in CSS; here we only toggle state classes. Runs on every render so
					// ad refresh that swaps creative types stays in sync.
					const inContentContainer = slotElement?.closest(
						'.pmd-ad-position-in-article'
					) ?? null;

					if ( ! inContentContainer ) {
						return;
					}

					inContentContainer.classList.toggle( 'pmd-ad-interscroller', isInterscroller );
					inContentContainer.classList.toggle( 'pmd-ad-standard-billboard', ! isInterscroller );

					self.holdInContentHeight( divId, inContentContainer, slotElement, isInterscroller );
				} );
			} );
		},

		/**
		 * Display a specific slot.
		 *
		 * @param {string} divId The div element ID.
		 */
		displaySlot: function( divId ) {
			googletag.cmd.push( function() {
				googletag.display( divId );
			} );
		},

		/**
		 * Display all slots on the page.
		 *
		 * Only displays slots that were actually defined in defineAllSlots().
		 * Slots filtered out by hasActiveSizes() (e.g. right-rail on mobile)
		 * are not in definedSlots and will not trigger a GPT request.
		 */
		displayAllSlots: function() {
			var definedIds = {};
			this.definedSlots.forEach( function( slot ) {
				definedIds[ slot.getSlotElementId() ] = true;
			} );

			var adSlots = document.querySelectorAll( '.pmd-ad-slot[data-slot-id], .pmd-native-ad-slot[data-slot-id]' );

			adSlots.forEach( function( element ) {
				if ( element.id && definedIds[ element.id ] ) {
					this.displaySlot( element.id );
				}
			}.bind( this ) );
		},

		/**
		 * Initialize content ad placeholders.
		 * Converts PHP-injected placeholder divs into proper ad containers.
		 */
		initContentAdPlaceholders: function() {
			var placeholders = document.querySelectorAll( '.pmd-ad-content-placeholder' );

			if ( ! placeholders.length ) {
				return;
			}

			placeholders.forEach( function( placeholder ) {
				var slotId = placeholder.getAttribute( 'data-ad-slot' );
				var position = placeholder.getAttribute( 'data-ad-position' );

				if ( ! slotId || ! this.contentSlots[ slotId ] ) {
					return;
				}

				// This runs again whenever slots are registered after page load
				// (registerContentSlots), so skip placeholders already wrapped
				// rather than giving them a second ad div.
				if ( placeholder.querySelector( '.pmd-ad-slot' ) ) {
					return;
				}

				var slotConfig = this.contentSlots[ slotId ];
				var divId = 'ad-in-' + position;

				// Add ad container classes to the placeholder.
				// The placeholder keeps its PHP-rendered min-height throughout.
				placeholder.classList.add( 'pmd-ad-container', 'pmd-ad-position-in-article', 'pmd-ad-content' );

				// Create the ad slot div inside the placeholder.
				var adSlot = document.createElement( 'div' );
				adSlot.id = divId;
				adSlot.className = 'pmd-ad-slot';
				adSlot.setAttribute( 'data-slot-id', slotId );
				adSlot.setAttribute(
					'data-slot-type',
					placeholder.getAttribute( 'data-slot-type' ) || SLOT_TYPE_DISPLAY
				);
				adSlot.setAttribute( 'data-sizes', JSON.stringify( slotConfig.sizes ) );

				placeholder.appendChild( adSlot );

			}.bind( this ) );
		},

		/**
		 * Register in-content ad slots whose placeholders reached the DOM after
		 * page load, and put them into a GPT request.
		 *
		 * A subscriber-only article is never served in full from the server —
		 * the paywall trims it to a lede and swaps the real body in over REST
		 * once Engage confirms the subscription. defineAllSlots() has long since
		 * walked that trimmed lede, which is too short to carry the in-content
		 * slots (the full-width / interscroller one above all), so those slots
		 * exist only in the hydrated body and its accompanying config. See
		 * pmd-paywall/assets/js/frontend/loader.js.
		 *
		 * @param {Object} slots Slot config keyed by slot ID, as built by ContentAdInjector::get_slot_data().
		 */
		registerContentSlots: function( slots ) {
			var self = this;

			if ( ! this.initialized || ! slots || 'object' !== typeof slots ) {
				return;
			}

			if ( 'undefined' === typeof googletag || ! googletag.cmd ) {
				return;
			}

			var slotIds = Object.keys( slots );
			if ( ! slotIds.length ) {
				return;
			}

			slotIds.forEach( function( slotId ) {
				self.contentSlots[ slotId ] = slots[ slotId ];
			} );

			/*
			 * Order matters: the replaced body took the previous placeholders'
			 * divs with it, and GPT still holds slots pointing at them. Release
			 * those before initContentAdPlaceholders() recreates divs under the
			 * same IDs, or the detached-slot check below stops recognising them
			 * and GPT rejects the redefinition.
			 */
			this.destroyDetachedContentSlots();

			// Wrap the new placeholders, then recompute the DOM-order maps so
			// `loc` and `ad_slot` targeting count the slots that exist now
			// rather than the ones that existed at page load.
			this.initContentAdPlaceholders();
			this.buildLocMap();
			this.buildAdSlotMap();

			var pending = [];

			slotIds.forEach( function( slotId ) {
				var slotConfig = self.contentSlots[ slotId ];
				var divId = 'ad-in-' + slotConfig.position;
				var element = document.getElementById( divId );

				if ( ! element ) {
					return;
				}

				pending.push( {
					element: element,
					slotId: slotId,
					config: slotConfig,
					divId: divId
				} );
			} );

			if ( ! pending.length ) {
				return;
			}

			googletag.cmd.push( function() {
				pending.forEach( function( item ) {
					self.defineContentSlot( item.element, item.slotId, item.config );
				} );
			} );

			// Mirrors init(): define, then bid, then display. enableServices()
			// already ran, and slots defined after it still display normally.
			this.runContentSlotAuction( pending, function() {
				googletag.cmd.push( function() {
					pending.forEach( function( item ) {
						googletag.display( item.divId );
					} );
				} );
			} );
		},

		/**
		 * Run a header-bidding auction limited to late-registered content slots.
		 *
		 * Uses refreshBids() rather than runAuction() because the page-load
		 * auction has already completed — runAuction() would find bidsRequested
		 * still set and return without asking for a bid.
		 *
		 * @param {Array}    pending  Slot descriptors built by registerContentSlots().
		 * @param {Function} callback Invoked once the auction settles, or immediately when bidding is off.
		 */
		runContentSlotAuction: function( pending, callback ) {
			var headerBiddingConfig = this.config.headerBidding || {};

			if ( ! headerBiddingConfig.enabled || ! window.pmdAds.headerBidding ) {
				callback();
				return;
			}

			var slots = pending.map( function( item ) {
				return {
					divId: item.divId,
					slotId: item.slotId,
					path: item.config.path,
					sizes: item.config.sizes || []
				};
			} );

			window.pmdAds.headerBidding.refreshBids( slots, callback );
		},

		/**
		 * Release in-content GPT slots whose div has left the document.
		 *
		 * Only reachable when something replaces the article body after the
		 * slots inside it were defined — today, the paywall swapping in the full
		 * body. Left alone, GPT keeps rendering into detached nodes and refuses
		 * to define a new slot for the same div ID.
		 *
		 * Scoped to in-content slots on purpose: a page-level slot missing from
		 * the DOM means something else is going on, and tearing it down here
		 * would be guesswork.
		 */
		destroyDetachedContentSlots: function() {
			var self = this;
			var contentDivIds = {};

			Object.keys( this.contentSlots || {} ).forEach( function( slotId ) {
				contentDivIds[ 'ad-in-' + self.contentSlots[ slotId ].position ] = true;
			} );

			var detached = this.definedSlots.filter( function( slot ) {
				var divId = slot.getSlotElementId();
				return contentDivIds[ divId ] && ! document.getElementById( divId );
			} );

			if ( ! detached.length ) {
				return;
			}

			detached.forEach( function( slot ) {
				var divId = slot.getSlotElementId();
				var state = self.slotRefreshStates[ divId ];

				if ( state ) {
					// Also drops the state entry and its viewability observer.
					self.stopSlotRefresh( state );
				}

				delete self.interscrollerRefreshBlockedSlots[ divId ];
				delete self.locMap[ divId ];
				delete self.adSlotMap[ divId ];
			} );

			this.definedSlots = this.definedSlots.filter( function( slot ) {
				return -1 === detached.indexOf( slot );
			} );
			this.renderedSlots = this.renderedSlots.filter( function( slot ) {
				return -1 === detached.indexOf( slot );
			} );

			googletag.cmd.push( function() {
				googletag.destroySlots( detached );
			} );
		},

		/**
		 * Subscribe to Viafoura comment ad requests.
		 *
		 * Viafoura runs in Google Ad Manager mode: it opens a promo element
		 * between comments (spacing is set in the Viafoura dashboard) and
		 * publishes its id. Mirrors legacy fem.js: a child ad div is added
		 * inside that element.
		 */
		bindViafouraAds: function() {
			var self = this;
			var viafouraConfig = this.config.viafouraAds || {};

			if ( ! viafouraConfig.enabled || ! viafouraConfig.adPath ) {
				return;
			}

			window.vfQ = window.vfQ || [];
			window.vfQ.push( function() {
				if ( ! window.vf || typeof window.vf.$subscribe !== 'function' ) {
					return;
				}

				window.vf.$subscribe( 'vf-ads', 'requestCommentAd', function( slotName ) {
					self.requestViafouraAd( slotName );
				} );
			} );
		},

		/**
		 * Handle a Viafoura comment ad request, queueing it until GPT services are enabled.
		 *
		 * @param {string} slotName Id of the Viafoura promo element.
		 */
		requestViafouraAd: function( slotName ) {
			if ( ! slotName || typeof slotName !== 'string' ) {
				return;
			}

			if ( ! this.viafouraAdsReady ) {
				this.viafouraAdQueue.push( slotName );
				return;
			}

			this.defineViafouraAd( slotName );
		},

		/**
		 * Fill requests that arrived before GPT services were enabled.
		 */
		flushViafouraAdRequests: function() {
			var queue = this.viafouraAdQueue;

			this.viafouraAdsReady = true;
			this.viafouraAdQueue = [];

			queue.forEach( function( slotName ) {
				this.defineViafouraAd( slotName );
			}.bind( this ) );
		},

		/**
		 * Add, bid on and display one comment ad inside a Viafoura promo element.
		 *
		 * Targeting per ad ops: prtn=viafoura, loc=comN, ad_slot=in_comments_N_LDR,
		 * slot=display, refresh, rc and an ad_cfp of
		 * {platform}_{page}_display_viafoura_{N}_{sizes}_rc{n}, with N counting from 1.
		 *
		 * @param {string} slotName Id of the Viafoura promo element.
		 */
		defineViafouraAd: function( slotName ) {
			var self = this;
			var viafouraConfig = this.config.viafouraAds || {};
			var container = document.getElementById( slotName );

			if ( ! container ) {
				return;
			}

			// Viafoura publishes again when a promo reloads. The same element
			// keeps its ad; a re-rendered element is a new node without one.
			if ( container.querySelector( '.pmd-viafoura-ad-slot' ) ) {
				return;
			}

			this.destroyDetachedViafouraSlots();

			var position = String( this.viafouraAdCount + 1 );
			var divId = 'ad-com-' + position;
			var sizes = viafouraConfig.sizes || [];

			if ( ! sizes.length || document.getElementById( divId ) ) {
				return;
			}

			this.viafouraAdCount++;

			// No data-slot-id, so the DOM-order loc/ad_slot maps and the
			// page-load auction leave this slot alone.
			var element = document.createElement( 'div' );
			element.id = divId;
			element.className = 'pmd-ad-slot pmd-viafoura-ad-slot';
			element.setAttribute( 'data-slot-type', SLOT_TYPE_DISPLAY );
			container.appendChild( element );

			googletag.cmd.push( function() {
				var slot = googletag.defineSlot( viafouraConfig.adPath, sizes, divId );

				if ( ! slot ) {
					return;
				}

				self.setSlotTargeting( slot, 'prtn', 'viafoura' );
				self.setSlotTypeTargeting( slot, SLOT_TYPE_DISPLAY );
				self.setSlotTargeting( slot, 'loc', 'com' + position );
				self.setSlotTargeting( slot, TARGETING_KEY_AD_SLOT, 'in_comments_' + position + '_LDR' );
				self.setSlotTargeting( slot, TARGETING_KEY_REFRESH, self.config.adRefreshEnabled ? 'true' : 'false' );
				self.setSlotTargeting( slot, 'rc', '0' );
				self.setAdFingerprint( slot, self.getDisplayFingerprintData( position, 'comments', sizes, element, 0, 'viafoura' ) );

				slot.addService( googletag.pubads() );
				self.definedSlots.push( slot );
			} );

			this.runContentSlotAuction(
				[ { divId: divId, slotId: divId, config: { path: viafouraConfig.adPath, sizes: sizes } } ],
				function() {
					googletag.cmd.push( function() {
						googletag.display( divId );
					} );
				}
			);
		},

		/**
		 * Release comment ad slots whose div left the page.
		 *
		 * Viafoura only cleans up slots on its own promo id, so ours are
		 * dropped here when a sort, tab switch or reload removes the comment list.
		 */
		destroyDetachedViafouraSlots: function() {
			var self = this;

			var detached = this.definedSlots.filter( function( slot ) {
				var divId = slot.getSlotElementId();
				return divId.indexOf( 'ad-com-' ) === 0 && ! document.getElementById( divId );
			} );

			if ( ! detached.length ) {
				return;
			}

			detached.forEach( function( slot ) {
				var state = self.slotRefreshStates[ slot.getSlotElementId() ];

				if ( state ) {
					self.stopSlotRefresh( state );
				}
			} );

			this.definedSlots = this.definedSlots.filter( function( slot ) {
				return -1 === detached.indexOf( slot );
			} );
			this.renderedSlots = this.renderedSlots.filter( function( slot ) {
				return -1 === detached.indexOf( slot );
			} );

			googletag.cmd.push( function() {
				googletag.destroySlots( detached );
			} );
		},

		/**
		 * Define a single content (in-article) ad slot.
		 * Must be called inside a googletag.cmd.push() callback.
		 *
		 * @param {Element} element    The slot DOM element.
		 * @param {string}  slotId     Content slot identifier (e.g. "ad-in1").
		 * @param {Object}  slotConfig Config from pmdContentAdSlots.
		 */
		defineContentSlot: function( element, slotId, slotConfig ) {
			var divId = element.id;

			// Use the full ad path from PHP (built by PathBuilder).
			var adPath = slotConfig.path || '/' + slotId;

			var slot = googletag.defineSlot(
				adPath,
				slotConfig.sizes,
				divId
			);

			if ( ! slot ) {
				return;
			}

			// Add responsive size mapping for content ads using configured breakpoints.
			// The md lower bound comes from PHP: 960 on v2 story pages (where the
			// >960px right-rail sizes apply) and 729 elsewhere.
			var mdBreakpoint = slotConfig.mdBreakpoint || CONTENT_BP_MD;
			var mapping = googletag.sizeMapping();
			var contentSizeMapping = [];
			if ( slotConfig.sizesLg && slotConfig.sizesLg.length ) {
				mapping = mapping.addSize( [ CONTENT_BP_LG, 0 ], slotConfig.sizesLg );
				contentSizeMapping.push( { viewport: [ CONTENT_BP_LG, 0 ], sizes: slotConfig.sizesLg } );
			}
			if ( slotConfig.sizesMd && slotConfig.sizesMd.length ) {
				mapping = mapping.addSize( [ mdBreakpoint, 0 ], slotConfig.sizesMd );
				contentSizeMapping.push( { viewport: [ mdBreakpoint, 0 ], sizes: slotConfig.sizesMd } );
			}
			if ( slotConfig.sizesSm && slotConfig.sizesSm.length ) {
				mapping = mapping.addSize( [ 0, 0 ], slotConfig.sizesSm );
				contentSizeMapping.push( { viewport: [ 0, 0 ], sizes: slotConfig.sizesSm } );
			}

			slot.defineSizeMapping( mapping.build() );

			// Set slot-level pos targeting only when active size is interscroller.
			var activeContentSizes = this.getActiveDisplayFingerprintSizes(
				slotConfig.sizes || [],
				contentSizeMapping
			);
			this.setPosTargeting(
				slot,
				activeContentSizes,
				'in-article-' + slotConfig.position
			);
			this.setSlotTypeTargeting(
				slot,
				this.getSlotTypeTargeting( element, SLOT_TYPE_DISPLAY )
			);

			// Set loc from pre-computed DOM-order map.
			var loc = this.locMap[ divId ] || slotId;
			this.setSlotTargeting( slot, 'loc', loc );

			// Set standardized ad_slot targeting (VIP-340).
			this.setAdSlotTargeting( slot, divId );

			// Add refresh targeting.
			var refreshEnabled = this.config.adRefreshEnabled ? 'true' : 'false';
			this.setSlotTargeting( slot, TARGETING_KEY_REFRESH, refreshEnabled );
			this.setSlotTargeting( slot, 'rc', '0' );
			this.setAdFingerprint( slot, this.getDisplayFingerprintData( loc, 'in-article', activeContentSizes, element, 0 ) );
			this.setSlotRefreshInterval( divId, slotConfig.refreshInterval );

			slot.addService( googletag.pubads() );
			this.definedSlots.push( slot );
		},

		/**
		 * Filter sizes for a specific viewport width.
		 *
		 * @param {Array} sizes All available sizes.
		 * @param {number} maxWidth Maximum width for the viewport.
		 * @return {Array} Filtered sizes.
		 */
		filterSizesForViewport: function( sizes, maxWidth ) {
			return sizes.filter( function( size ) {
				return size[ 0 ] <= maxWidth;
			} );
		},

		/**
		 * Parse sizes from string format to array format.
		 *
		 * @param {string} sizesString Sizes string like "970x90, 728x90".
		 * @return {Array} Array of [width, height] pairs.
		 */
		/**
		 * Remove duplicate sizes from an array of [width, height] pairs.
		 *
		 * @param {Array} sizes Array of [width, height] pairs.
		 * @return {Array} Deduplicated sizes.
		 */
		deduplicateSizes: function( sizes ) {
			var seen = {};
			return sizes.filter( function( size ) {
				var key = size[ 0 ] + 'x' + size[ 1 ];
				if ( seen[ key ] ) {
					return false;
				}
				seen[ key ] = true;
				return true;
			} );
		},

		parseSizesString: function( sizesString ) {
			if ( ! sizesString || typeof sizesString !== 'string' ) {
				return [];
			}

			var sizes = [];
			var sizeStrings = sizesString.split( ',' );

			sizeStrings.forEach( function( s ) {
				var parts = s.trim().split( 'x' );
				if ( parts.length === 2 ) {
					var width = parseInt( parts[ 0 ], 10 );
					var height = parseInt( parts[ 1 ], 10 );
					if ( ! isNaN( width ) && ! isNaN( height ) ) {
						sizes.push( [ width, height ] );
					}
				}
			} );

			return sizes;
		},

		/**
		 * Define a single page-injected ad slot.
		 * Must be called inside a googletag.cmd.push() callback.
		 *
		 * @param {Element} element The slot DOM element.
		 */
		definePageSlot: function( element ) {
			var slotId = element.getAttribute( 'data-slot-id' );
			var divId = element.id;

			if ( ! slotId || ! divId ) {
				return;
			}

			// Get breakpoint-specific sizes from data attributes.
			var sizesFw = this.parseSizesString( element.getAttribute( 'data-sizes-fw' ) );
			var sizesStandard = this.parseSizesString( element.getAttribute( 'data-sizes-standard' ) );
			var sizesMobile = this.parseSizesString( element.getAttribute( 'data-sizes-mobile' ) );

			// Combine all sizes for slot definition.
			var allSizes = [].concat( sizesFw, sizesStandard, sizesMobile );

			// Remove duplicates.
			var uniqueSizes = [];
			var sizeMap = {};
			allSizes.forEach( function( size ) {
				var key = size[ 0 ] + 'x' + size[ 1 ];
				if ( ! sizeMap[ key ] ) {
					sizeMap[ key ] = true;
					uniqueSizes.push( size );
				}
			} );

			if ( ! uniqueSizes.length ) {
				return;
			}

			// Build ad path — use dynamic path from PHP if available.
			var adPath = element.getAttribute( 'data-ad-path' );
			if ( ! adPath ) {
				var networkId = this.config.gam ? this.config.gam.networkId : '';
				adPath = '/' + networkId + '/page';
			}

			// Define the slot with all possible sizes.
			var slot = googletag.defineSlot( adPath, uniqueSizes, divId );

			if ( ! slot ) {
				return;
			}

			// Size mapping: each viewport only gets the sizes configured for that breakpoint.
			// Full-width (≥1200): fw sizes only, fallback to standard.
			// Standard (768-1199): standard sizes only, fallback to fw.
			// Mobile (<768): mobile sizes only, empty if not configured.
			var pageSizesAt1200 = sizesFw.length ? sizesFw : ( sizesStandard.length ? sizesStandard : uniqueSizes );
			var pageSizesAt768 = sizesStandard.length ? sizesStandard : ( sizesFw.length ? sizesFw : uniqueSizes );
			var pageSizesAt0 = sizesMobile.length ? sizesMobile : [];

			var pageSizeMapping = [
				{ viewport: [ 1200, 0 ], sizes: pageSizesAt1200 },
				{ viewport: [ 768, 0 ], sizes: pageSizesAt768 },
				{ viewport: [ 0, 0 ], sizes: pageSizesAt0 },
			];
			var mapping = googletag.sizeMapping()
				.addSize( [ 1200, 0 ], pageSizesAt1200 )
				.addSize( [ 768, 0 ], pageSizesAt768 )
				.addSize( [ 0, 0 ], pageSizesAt0 )
				.build();

			slot.defineSizeMapping( mapping );

			// Set loc from pre-computed DOM-order map.
			var loc = this.locMap[ divId ] || slotId;
			this.setSlotTargeting( slot, 'loc', loc );
			var activePageSizes = this.getActiveDisplayFingerprintSizes( uniqueSizes, pageSizeMapping );
			this.setPosTargeting(
				slot,
				activePageSizes,
				'page-' + loc
			);
			this.setSlotTypeTargeting(
				slot,
				this.getSlotTypeTargeting( element, SLOT_TYPE_DISPLAY )
			);

			// Set standardized ad_slot targeting (VIP-340).
			this.setAdSlotTargeting( slot, divId );

			// Add refresh targeting.
			var refreshEnabled = this.config.adRefreshEnabled ? 'true' : 'false';
			this.setSlotTargeting( slot, TARGETING_KEY_REFRESH, refreshEnabled );
			this.setSlotTargeting( slot, 'rc', '0' );
			this.setAdFingerprint( slot, this.getDisplayFingerprintData( loc, 'page', activePageSizes, element, 0 ) );
			this.setSlotRefreshInterval( divId, element.getAttribute( 'data-refresh-interval' ) );

			slot.addService( googletag.pubads() );
			this.definedSlots.push( slot );
		},

		/**
		 * Define a single native ad slot with fluid sizing.
		 * Must be called inside a googletag.cmd.push() callback.
		 *
		 * @param {Element} element The native slot DOM element.
		 */
		defineNativeSlot: function( element ) {
			var slotId = element.getAttribute( 'data-slot-id' );
			var divId = element.id;

			if ( ! slotId || ! divId ) {
				return;
			}

			var nativeConfig = this.config.nativeAds || {};

			// Build native ad path — use dynamic base path from PHP if available.
			var adPath = nativeConfig.adPath || '';
			if ( ! adPath ) {
				var gamConfig = this.config.gam || {};
				var basePath = gamConfig.basePath || '';
				if ( basePath ) {
					adPath = basePath + '/native_infeed';
				} else {
					adPath = '/' + ( gamConfig.networkId || '' ) + '/native_infeed';
				}
			}

			// Define slot with fluid size for native ads.
			var slot = googletag.defineSlot( adPath, 'fluid', divId );

			if ( ! slot ) {
				return;
			}

			// Set loc from pre-computed DOM-order map.
			var loc = this.locMap[ divId ] || '1';
			this.setSlotTypeTargeting(
				slot,
				this.getSlotTypeTargeting( element, SLOT_TYPE_INFEED )
			);
			this.setSlotTargeting( slot, 'loc', loc );
			var pos = element.getAttribute( 'data-pos' ) || 'inline';
			this.setPosTargeting( slot, [], pos );

			// Set standardized ad_slot targeting (VIP-340).
			this.setAdSlotTargeting( slot, divId );

			this.setSlotTargeting( slot, TARGETING_KEY_REFRESH, 'false' );
			this.setSlotTargeting( slot, 'rc', '0' );
			this.setAdFingerprint( slot, this.getNativeFingerprintData( loc, element, 0 ) );

			slot.addService( googletag.pubads() );
			this.definedSlots.push( slot );
		},

		/**
		 * Start ad refresh interval.
		 * Only refreshes slots that have been rendered (compatible with lazy loading).
		 * Updates the refresh count (rc) targeting on each refresh.
		 * Also refreshes header bidding bids if enabled.
		 */
		startAdRefresh: function() {
			if ( ! this.config.adRefreshEnabled ) {
				return;
			}

			this.bindRefreshVisibilityHandlers();
		},

		/**
		 * Build slot configuration for header bidding refresh.
		 *
		 * @param {Object} gptSlot GPT slot instance.
		 * @return {Object|null} Slot configuration or null if invalid.
		 */
		buildSlotForRefresh: function( gptSlot ) {
			if ( ! gptSlot ) {
				return null;
			}

			var divId = gptSlot.getSlotElementId();
			var slotPath = gptSlot.getAdUnitPath();
			var sizes = gptSlot.getSizes() || [];

			if ( ! divId ) {
				return null;
			}

			// Convert GPT sizes to array format.
			var sizeArray = [];
			if ( sizes && sizes.length ) {
				sizes.forEach( function( size ) {
					if ( typeof size.getWidth === 'function' ) {
						sizeArray.push( [ size.getWidth(), size.getHeight() ] );
					} else if ( Array.isArray( size ) ) {
						sizeArray.push( size );
					}
				} );
			}

			return {
				divId: divId,
				slotId: divId,
				path: slotPath,
				sizes: sizeArray
			};
		},

		/**
		 * Determine whether a rendered slot is eligible for auto-refresh.
		 * Uses GPT targeting only so DOM/class changes cannot alter behavior.
		 *
		 * @param {Object} gptSlot GPT slot instance.
		 * @return {boolean} True when slot can be refreshed.
		 */
		isSlotRefreshEligible: function( gptSlot ) {
			if ( ! gptSlot ) {
				return false;
			}

			var refreshTargeting = this.getSlotTargetingValues( gptSlot, TARGETING_KEY_REFRESH );
			return Array.isArray( refreshTargeting ) && refreshTargeting.indexOf( 'true' ) !== -1;
		},

		/**
		 * Pause auto-refresh for a slot that has rendered an interscroller creative.
		 *
		 * Flags the slot so it is never (re)armed, and tears down any refresh
		 * timer a prior standard creative already started. This prevents the
		 * layout shift caused by refreshing the slot beneath a viewport-pinned
		 * interscroller (6x6/7x7), mirroring Cheetah's `data-ad-ref="off"`.
		 *
		 * @param {string} divId Slot div ID.
		 */
		blockRefreshForInterscroller: function( divId ) {
			if ( ! divId ) {
				return;
			}

			this.interscrollerRefreshBlockedSlots[ divId ] = true;

			var state = this.slotRefreshStates[ divId ];
			if ( state ) {
				this.stopSlotRefresh( state );
			}
		},

		/**
		 * Keep an in-content ad box at the tallest height its slot has reached.
		 *
		 * GAM Ad Expansion grows a mobile creative a few seconds after render, and
		 * the box grows with it. Each refresh then renders the creative at its
		 * original size, so without this the box shrinks back and re-expands on
		 * every refresh cycle — a visible layout shift each time. The height is
		 * exposed as --pmd-ad-held-height, which the placeholder's mobile
		 * min-height rule (ContentAdInjector) folds in via max().
		 *
		 * Interscrollers are skipped: they need the fixed height as their reveal
		 * window, so any held height is dropped when one renders.
		 *
		 * @param {string}      divId            Slot div ID.
		 * @param {HTMLElement} container        In-content ad container.
		 * @param {HTMLElement} slotElement      Slot div holding the creative.
		 * @param {boolean}     isInterscroller  Whether the render was an interscroller.
		 */
		holdInContentHeight: function( divId, container, slotElement, isInterscroller ) {
			if ( ! divId || ! slotElement || typeof window.ResizeObserver === 'undefined' ) {
				return;
			}

			var observer = this.inContentHeightObservers[ divId ];

			if ( isInterscroller ) {
				if ( observer ) {
					observer.disconnect();
					delete this.inContentHeightObservers[ divId ];
				}
				container.style.removeProperty( '--pmd-ad-held-height' );
				return;
			}

			// One observer per slot covers every later refresh.
			if ( observer ) {
				return;
			}

			var heldHeight = 0;

			observer = new window.ResizeObserver( function() {
				var height = slotElement.offsetHeight;

				if ( height > heldHeight ) {
					heldHeight = height;
					container.style.setProperty( '--pmd-ad-held-height', height + 'px' );
				}
			} );

			observer.observe( slotElement );
			this.inContentHeightObservers[ divId ] = observer;
		},

		/**
		 * Initialize refresh state for a rendered slot.
		 *
		 * @param {Object} gptSlot GPT slot instance.
		 */
		initSlotRefresh: function( gptSlot ) {
			if ( ! this.config.adRefreshEnabled || ! gptSlot ) {
				return;
			}

			var divId = gptSlot.getSlotElementId();
			if ( ! divId ) {
				return;
			}

			// Never re-arm refresh for a slot that has shown an interscroller.
			if ( this.interscrollerRefreshBlockedSlots[ divId ] ) {
				return;
			}

			if ( this.slotRefreshStates[ divId ] ) {
				return;
			}

			if ( ! this.isSlotRefreshEligible( gptSlot ) ) {
				return;
			}

			var maxRefreshes = parseInt( this.config.adRefreshMax, 10 );
			if ( isNaN( maxRefreshes ) || maxRefreshes < 1 ) {
				return;
			}

			var intervalSeconds = this.getSlotRefreshInterval( divId );
			if ( ! intervalSeconds || intervalSeconds < 10 ) {
				return;
			}

			var element = document.getElementById( divId );
			if ( ! element ) {
				return;
			}

			var state = {
				slot: gptSlot,
				divId: divId,
				element: element,
				intervalMs: intervalSeconds * 1000,
				maxRefreshes: maxRefreshes,
				refreshCount: 0,
				intervalElapsed: false,
				pending: false,
				timerId: null,
				inView: false,
				observer: null,
				viewCheckTimer: null
			};

			this.observeSlotViewability( state );
			this.slotRefreshStates[ divId ] = state;
			this.maybeStartSlotRefreshTimer( state );
		},

		/**
		 * Schedule the next refresh timer for a slot.
		 *
		 * @param {Object} state Slot refresh state.
		 */
		scheduleSlotRefresh: function( state ) {
			if ( ! state ) {
				return;
			}

			if ( state.timerId ) {
				clearTimeout( state.timerId );
			}

			state.intervalElapsed = false;
			state.timerId = setTimeout( function() {
				state.timerId = null;
				state.intervalElapsed = true;
				this.maybeRefreshSlot( state );
			}.bind( this ), state.intervalMs );
		},

		/**
		 * Start a slot refresh timer once the slot is eligible.
		 *
		 * @param {Object} state Slot refresh state.
		 */
		maybeStartSlotRefreshTimer: function( state ) {
			if ( ! state || state.timerId || state.intervalElapsed ) {
				return;
			}

			if ( state.refreshCount >= state.maxRefreshes ) {
				this.stopSlotRefresh( state );
				return;
			}

			if ( ! state.element || ! document.body.contains( state.element ) ) {
				this.stopSlotRefresh( state );
				return;
			}

			if ( ! state.inView || ! this.isActiveBrowser() ) {
				return;
			}

			this.scheduleSlotRefresh( state );
		},

		/**
		 * Attempt to refresh a slot if conditions are met.
		 *
		 * @param {Object} state Slot refresh state.
		 */
		maybeRefreshSlot: function( state ) {
			if ( ! state || ! state.intervalElapsed ) {
				return;
			}

			if ( state.refreshCount >= state.maxRefreshes ) {
				this.stopSlotRefresh( state );
				return;
			}

			if ( ! state.element || ! document.body.contains( state.element ) ) {
				this.stopSlotRefresh( state );
				return;
			}

			if ( ! state.observer ) {
				state.inView = this.isElementViewable( state.element, 0.5 );
			}

			if ( ! this.isActiveBrowser() || ! state.inView ) {
				state.pending = true;
				return;
			}

			state.pending = false;
			state.refreshCount++;

			this.refreshSlot( state );
			this.scheduleSlotRefresh( state );
		},

		/**
		 * Refresh a slot with header bidding support.
		 *
		 * @param {Object} state Slot refresh state.
		 */
		refreshSlot: function( state ) {
			var self = this;
			var slotConfig = this.buildSlotForRefresh( state.slot );

			var headerBiddingConfig = this.config.headerBidding || {};
			if ( headerBiddingConfig.enabled && window.pmdAds.headerBidding && slotConfig ) {
				window.pmdAds.headerBidding.refreshBids( [ slotConfig ], function() {
					self.performGPTRefresh( state );
				} );
			} else {
				self.performGPTRefresh( state );
			}
		},

		/**
		 * Perform the actual GPT slot refresh.
		 *
		 * @param {Object} state Slot refresh state.
		 */
		performGPTRefresh: function( state ) {
			var self = this;
			googletag.cmd.push( function() {
				self.setSlotTargeting( state.slot, 'rc', String( state.refreshCount ) );
				self.updateAdFingerprintRefreshCount( state.slot, state.refreshCount );
				googletag.pubads().refresh( [ state.slot ] );
			} );
		},

		/**
		 * Stop refresh tracking for a slot.
		 *
		 * @param {Object} state Slot refresh state.
		 */
		stopSlotRefresh: function( state ) {
			if ( ! state ) {
				return;
			}

			if ( state.timerId ) {
				clearTimeout( state.timerId );
			}

			if ( state.observer ) {
				state.observer.disconnect();
			}

			if ( state.viewCheckTimer ) {
				clearInterval( state.viewCheckTimer );
			}

			delete this.slotRefreshStates[ state.divId ];
		},

		/**
		 * Build a refresh interval map from config slots.
		 *
		 * @return {Object} Map of divId -> refresh interval in seconds.
		 */
		buildSlotRefreshIntervalMap: function() {
			var map = {};
			var slots = this.config.slots || [];

			slots.forEach( function( slotConfig ) {
				if ( ! slotConfig || ! slotConfig.divId ) {
					return;
				}

				var parsed = parseInt( slotConfig.refreshInterval, 10 );
				if ( ! isNaN( parsed ) && parsed >= 10 ) {
					map[ slotConfig.divId ] = parsed;
				}
			} );

			return map;
		},

		/**
		 * Set refresh interval for a slot.
		 *
		 * @param {string} divId Slot div ID.
		 * @param {string|number|null} interval Refresh interval in seconds.
		 */
		setSlotRefreshInterval: function( divId, interval ) {
			if ( ! divId ) {
				return;
			}

			var parsed = parseInt( interval, 10 );
			if ( ! isNaN( parsed ) && parsed >= 10 ) {
				this.slotRefreshIntervals[ divId ] = parsed;
			}
		},

		/**
		 * Get refresh interval for a slot, falling back to default.
		 *
		 * @param {string} divId Slot div ID.
		 * @return {number} Refresh interval in seconds.
		 */
		getSlotRefreshInterval: function( divId ) {
			if ( divId && this.slotRefreshIntervals[ divId ] ) {
				return this.slotRefreshIntervals[ divId ];
			}

			return this.config.adRefreshInterval || 10;
		},

		/**
		 * Bind visibility and focus handlers for pending refreshes.
		 */
		bindRefreshVisibilityHandlers: function() {
			if ( this.refreshVisibilityHandlersBound ) {
				return;
			}

			this.refreshVisibilityHandlersBound = true;

			var self = this;
			var handler = function() {
				Object.keys( self.slotRefreshStates ).forEach( function( divId ) {
					var state = self.slotRefreshStates[ divId ];
					if ( ! state ) {
						return;
					}

					state.inView = self.isElementViewable( state.element, 0.5 );
					self.maybeStartSlotRefreshTimer( state );

					if ( state.intervalElapsed ) {
						self.maybeRefreshSlot( state );
					}
				} );
			};

			document.addEventListener( 'visibilitychange', handler );
			window.addEventListener( 'focus', handler );

			// Fallback for cases where IntersectionObserver doesn't fire on re-entry.
			var scheduled = false;
			var onScroll = function() {
				if ( scheduled ) {
					return;
				}
				scheduled = true;
				window.requestAnimationFrame( function() {
					scheduled = false;
					handler();
				} );
			};
			window.addEventListener( 'scroll', onScroll, { passive: true } );
			window.addEventListener( 'resize', onScroll );
		},

		/**
		 * Observe viewability for a slot.
		 *
		 * @param {Object} state Slot refresh state.
		 */
		observeSlotViewability: function( state ) {
			if ( ! state || ! state.element ) {
				return;
			}

			state.inView = this.isElementViewable( state.element, 0.5 );

			if ( ! ( 'IntersectionObserver' in window ) ) {
				state.viewCheckTimer = setInterval( function() {
					if ( ! state.element ) {
						return;
					}

					state.inView = this.isElementViewable( state.element, 0.5 );
					this.maybeStartSlotRefreshTimer( state );
					if ( state.intervalElapsed ) {
						this.maybeRefreshSlot( state );
					}
				}.bind( this ), 1000 );
				return;
			}

			var self = this;
			state.observer = new IntersectionObserver( function( entries ) {
				entries.forEach( function( entry ) {
					if ( entry.target !== state.element ) {
						return;
					}

					state.inView = entry.intersectionRatio >= 0.5;
					self.maybeStartSlotRefreshTimer( state );

					if ( state.intervalElapsed ) {
						self.maybeRefreshSlot( state );
					}
				} );
			}, {
				threshold: [ 0, 0.5, 1 ]
			} );

			state.observer.observe( state.element );
		},

		/**
		 * Check if the page is active and visible.
		 *
		 * @return {boolean} True if the page is active.
		 */
		isActiveBrowser: function() {
			if ( document.visibilityState && document.visibilityState !== 'visible' ) {
				return false;
			}

			if ( typeof document.hasFocus === 'function' && ! document.hasFocus() ) {
				return false;
			}

			return true;
		},

		/**
		 * Check if an element meets a visibility threshold.
		 *
		 * @param {Element} element DOM element.
		 * @param {number} threshold Visibility threshold (0-1).
		 * @return {boolean} True if threshold is met.
		 */
		isElementViewable: function( element, threshold ) {
			if ( ! element || ! element.getBoundingClientRect ) {
				return false;
			}

			var rect = element.getBoundingClientRect();
			var viewportWidth = window.innerWidth || document.documentElement.clientWidth;
			var viewportHeight = window.innerHeight || document.documentElement.clientHeight;

			if ( rect.width <= 0 || rect.height <= 0 ) {
				return false;
			}

			var visibleWidth = Math.min( rect.right, viewportWidth ) - Math.max( rect.left, 0 );
			var visibleHeight = Math.min( rect.bottom, viewportHeight ) - Math.max( rect.top, 0 );

			if ( visibleWidth <= 0 || visibleHeight <= 0 ) {
				return false;
			}

			var visibleArea = visibleWidth * visibleHeight;
			var totalArea = rect.width * rect.height;

			return ( visibleArea / totalArea ) >= threshold;
		},

		/**
		 * Check if debug mode is enabled.
		 *
		 * @return {boolean} True if debug mode is enabled.
		 */
		isDebugMode: function() {
			return document.body.classList.contains( 'pmd-ads-debug' );
		},

		/**
		 * Format sizes array to display string.
		 *
		 * @param {Array} sizes Array of [width, height] pairs.
		 * @return {string} Formatted string like "970x90, 728x90".
		 */
		formatSizesArray: function( sizes ) {
			if ( ! sizes || ! Array.isArray( sizes ) || ! sizes.length ) {
				return '';
			}

			return sizes.map( function( size ) {
				if ( Array.isArray( size ) && size.length >= 2 ) {
					return size[ 0 ] + 'x' + size[ 1 ];
				}
				return '';
			} ).filter( Boolean ).join( ', ' );
		},

		/**
		 * Get sizes from sizeMapping for current viewport.
		 *
		 * @param {Array} sizeMapping Array of {viewport, sizes} objects.
		 * @return {Array|null} Sizes array for current viewport or null.
		 */
		getSizesFromMapping: function( sizeMapping ) {
			if ( ! sizeMapping || ! Array.isArray( sizeMapping ) || ! sizeMapping.length ) {
				return null;
			}

			var viewportWidth = window.innerWidth;

			// Sort by viewport width descending to find the best match.
			var sortedMapping = sizeMapping.slice().sort( function( a, b ) {
				var aWidth = ( a.viewport && a.viewport[ 0 ] ) || 0;
				var bWidth = ( b.viewport && b.viewport[ 0 ] ) || 0;
				return bWidth - aWidth;
			} );

			// Find the first mapping where viewport width is <= current width.
			for ( var i = 0; i < sortedMapping.length; i++ ) {
				var mapping = sortedMapping[ i ];
				var minWidth = ( mapping.viewport && mapping.viewport[ 0 ] ) || 0;

				if ( viewportWidth >= minWidth ) {
					return mapping.sizes || [];
				}
			}

			return null;
		},

		/**
		 * Get sizes display string for current viewport.
		 * For slots with sizeMapping (leaderboard, page), shows only relevant sizes.
		 *
		 * @param {Element} slot The ad slot element.
		 * @return {string} Sizes display string.
		 */
		getSizesForCurrentViewport: function( slot ) {
			var viewportWidth = window.innerWidth;
			var slotId = slot.getAttribute( 'data-slot-id' );
			var divId = slot.id;
			var configSlots = this.config.slots || [];
			var configSlot = null;

			for ( var i = 0; i < configSlots.length; i++ ) {
				if ( configSlots[ i ].divId === divId || configSlots[ i ].slotId === slotId ) {
					configSlot = configSlots[ i ];
					break;
				}
			}

			// For content ad slots, use viewport-specific sizes from slot config.
			var contentSlots = this.contentSlots || {};
			if ( slotId && slotId.indexOf( 'ad-in' ) === 0 && contentSlots[ slotId ] ) {
				var contentConfig = contentSlots[ slotId ];
				var vpSizes = null;

				var contentBpMd = contentConfig.mdBreakpoint || CONTENT_BP_MD;

				if ( viewportWidth >= CONTENT_BP_LG && contentConfig.sizesLg && contentConfig.sizesLg.length ) {
					vpSizes = contentConfig.sizesLg;
				} else if ( viewportWidth >= contentBpMd && contentConfig.sizesMd && contentConfig.sizesMd.length ) {
					vpSizes = contentConfig.sizesMd;
				} else if ( contentConfig.sizesSm && contentConfig.sizesSm.length ) {
					vpSizes = contentConfig.sizesSm;
				}

				if ( vpSizes ) {
					return this.formatSizesArray( vpSizes );
				}
				return this.formatSizesArray( contentConfig.sizes || [] );
			}

			// For homepage slots, use breakpoint-specific data attributes.
			if ( configSlot && configSlot.position === 'page' ) {
				var sizesFw = slot.getAttribute( 'data-sizes-fw' );
				var sizesStandard = slot.getAttribute( 'data-sizes-standard' );
				var sizesMobile = slot.getAttribute( 'data-sizes-mobile' );

				if ( viewportWidth >= 1200 && sizesFw ) {
					return sizesFw;
				} else if ( viewportWidth >= 768 && sizesStandard ) {
					return sizesStandard;
				} else if ( sizesMobile ) {
					return sizesMobile;
				}
			}

			// For leaderboard/global slots, check config for sizeMapping.
			if ( configSlot ) {
				if ( configSlot.sizeMapping && configSlot.sizeMapping.length ) {
					var mappedSizes = this.getSizesFromMapping( configSlot.sizeMapping );
					if ( mappedSizes ) {
						return this.formatSizesArray( mappedSizes );
					}
				}
				// Fall back to slot's configured sizes.
				if ( configSlot.sizes ) {
					return this.formatSizesArray( configSlot.sizes );
				}
			}

			// For other slots, use the general data-sizes attribute.
			var sizesAttr = slot.getAttribute( 'data-sizes' );

			if ( ! sizesAttr ) {
				return '';
			}

			// Try to parse as JSON array.
			try {
				var sizesArray = JSON.parse( sizesAttr );
				if ( Array.isArray( sizesArray ) ) {
					return this.formatSizesArray( sizesArray );
				}
			} catch ( e ) {
				// If not JSON, assume it's already formatted.
				return sizesAttr;
			}

			return sizesAttr;
		},

		/**
		 * Record a performance timing mark (debug mode only).
		 *
		 * @param {string} name Mark name (without prefix).
		 */
		perfMark: function( name ) {
			if ( ! this.perfTimings ) {
				return;
			}
			this.perfTimings[ name ] = performance.now();
		},

		/**
		 * Track per-slot render times via GPT slotRenderEnded (debug mode only).
		 */
		initPerfSlotTracking: function() {
			if ( ! this.perfTimings ) {
				return;
			}

			var self = this;

			googletag.cmd.push( function() {
				googletag.pubads().addEventListener( 'slotRenderEnded', function( event ) {
					var divId = event.slot.getSlotElementId();
					self.perfTimings[ 'slot-rendered:' + divId ] = performance.now();
					self.updatePerfSlotRow( divId );
				} );
			} );
		},

		/**
		 * Build the performance timing debug panel (debug mode only).
		 * Inserted as a fixed panel at the bottom of the viewport.
		 */
		buildPerfTimingPanel: function() {
			if ( ! this.perfTimings ) {
				return;
			}

			var t = this.perfTimings;
			var nav = 0; // performance.now() is relative to navigationStart.

			var measures = [];

			if ( t[ 'init-start' ] !== undefined ) {
				measures.push( { label: 'JS Init Start', ms: t[ 'init-start' ] } );
			}
			if ( t[ 'gpt-ready' ] !== undefined ) {
				measures.push( { label: 'GPT Ready', ms: t[ 'gpt-ready' ] } );
			}
			if ( t[ 'slots-defined' ] !== undefined ) {
				measures.push( { label: 'Slots Defined', ms: t[ 'slots-defined' ] } );
			}
			if ( t[ 'targeting-done' ] !== undefined ) {
				measures.push( { label: 'Targeting Done', ms: t[ 'targeting-done' ] } );
			}
			if ( t[ 'bidding-done' ] !== undefined ) {
				measures.push( { label: 'Bidding Done', ms: t[ 'bidding-done' ] } );
			}
			if ( t[ 'display-start' ] !== undefined ) {
				measures.push( { label: 'Display Start', ms: t[ 'display-start' ] } );
			}

			// Inject styles inline so the panel works without a CSS build.
			if ( ! document.getElementById( 'pmd-ad-perf-styles' ) ) {
				var style = document.createElement( 'style' );
				style.id = 'pmd-ad-perf-styles';
				style.textContent =
					'.pmd-ad-perf-panel{position:fixed;bottom:0;right:0;width:320px;max-height:80vh;overflow-y:auto;background:rgba(0,0,0,.92);color:#fff;font:12px/1.4 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,monospace;padding:12px;z-index:999999;border-top-left-radius:8px;box-shadow:-2px -2px 10px rgba(0,0,0,.3)}' +
					'.pmd-ad-perf-panel--collapsed{display:none}' +
					'.pmd-ad-perf-toggle{position:fixed;bottom:8px;right:328px;z-index:999999;background:#333;color:#4ecdc4;border:1px solid #4ecdc4;border-radius:4px;padding:4px 10px;font:11px monospace;cursor:pointer}' +
					'.pmd-ad-perf-toggle:hover{background:#4ecdc4;color:#000}' +
					'.pmd-ad-perf-title{font-weight:700;font-size:13px;color:#4ecdc4;margin-bottom:8px;border-bottom:1px solid #555;padding-bottom:4px}' +
					'.pmd-ad-perf-subtitle{font-weight:600;font-size:11px;color:#aaa;margin:10px 0 4px;text-transform:uppercase;letter-spacing:.5px}' +
					'.pmd-ad-perf-table{width:100%;border-collapse:collapse}' +
					'.pmd-ad-perf-table td{padding:2px 4px}' +
					'.pmd-ad-perf-table td:first-child{color:#ccc}' +
					'.pmd-ad-perf-table td:last-child{text-align:right;font-variant-numeric:tabular-nums}' +
					'.pmd-perf-good{color:#2ecc71}' +
					'.pmd-perf-warn{color:#f9ca24}' +
					'.pmd-perf-bad{color:#ff6b6b}';
				document.head.appendChild( style );
			}

			// Build panel.
			var panel = document.createElement( 'div' );
			panel.className = 'pmd-ad-perf-panel';

			var title = document.createElement( 'div' );
			title.className = 'pmd-ad-perf-title';
			title.textContent = 'Ad Load Timing';
			panel.appendChild( title );

			// Pipeline measures.
			var table = document.createElement( 'table' );
			table.className = 'pmd-ad-perf-table';

			measures.forEach( function( m ) {
				var row = document.createElement( 'tr' );

				var labelCell = document.createElement( 'td' );
				labelCell.textContent = m.label;
				row.appendChild( labelCell );

				var valueCell = document.createElement( 'td' );
				var ms = Math.round( m.ms );
				valueCell.textContent = ms + ' ms';
				valueCell.className = ms < 2000 ? 'pmd-perf-good' : ( ms < 4000 ? 'pmd-perf-warn' : 'pmd-perf-bad' );
				row.appendChild( valueCell );

				table.appendChild( row );
			} );

			panel.appendChild( table );

			// Computed durations.
			var durations = [];

			if ( t[ 'init-start' ] !== undefined && t[ 'display-start' ] !== undefined ) {
				durations.push( { label: 'Total Init', ms: t[ 'display-start' ] - t[ 'init-start' ] } );
			}
			if ( t[ 'init-start' ] !== undefined && t[ 'bidding-done' ] !== undefined ) {
				durations.push( { label: 'Header Bidding', ms: t[ 'bidding-done' ] - t[ 'init-start' ] } );
			}
			if ( t[ 'init-start' ] !== undefined && t[ 'targeting-done' ] !== undefined ) {
				durations.push( { label: 'Targeting', ms: t[ 'targeting-done' ] - t[ 'init-start' ] } );
			}
			if ( t[ 'display-start' ] !== undefined ) {
				durations.push( { label: 'Time to First Ad Request', ms: t[ 'display-start' ] } );
			}

			if ( durations.length ) {
				var durTitle = document.createElement( 'div' );
				durTitle.className = 'pmd-ad-perf-subtitle';
				durTitle.textContent = 'Durations';
				panel.appendChild( durTitle );

				var durTable = document.createElement( 'table' );
				durTable.className = 'pmd-ad-perf-table';

				durations.forEach( function( d ) {
					var row = document.createElement( 'tr' );

					var labelCell = document.createElement( 'td' );
					labelCell.textContent = d.label;
					row.appendChild( labelCell );

					var valueCell = document.createElement( 'td' );
					var ms = Math.round( d.ms );
					valueCell.textContent = ms + ' ms';
					valueCell.className = ms < 2000 ? 'pmd-perf-good' : ( ms < 4000 ? 'pmd-perf-warn' : 'pmd-perf-bad' );
					row.appendChild( valueCell );

					durTable.appendChild( row );
				} );

				panel.appendChild( durTable );
			}

			// Density (per-viewport) section — only when pmdAdsConfig.debug is present
			// (server gates this on the ?pmd_ads_debug=1 query var).
			var debugCfg = ( window.pmdAdsConfig && window.pmdAdsConfig.debug ) || null;
			if ( debugCfg && debugCfg.density ) {
				var density = debugCfg.density;
				var clientTier = document.documentElement.getAttribute( 'data-vp' ) || '';
				var serverTier = density.tier || '';

				var densityTitle = document.createElement( 'div' );
				densityTitle.className = 'pmd-ad-perf-subtitle';
				densityTitle.textContent = 'Density (per-viewport)';
				panel.appendChild( densityTitle );

				var tierTable = document.createElement( 'table' );
				tierTable.className = 'pmd-ad-perf-table';
				var addRow = function( table, label, value, cls ) {
					var row = document.createElement( 'tr' );
					var labelCell = document.createElement( 'td' );
					labelCell.textContent = label;
					row.appendChild( labelCell );
					var valueCell = document.createElement( 'td' );
					valueCell.textContent = value;
					if ( cls ) {
						valueCell.className = cls;
					}
					row.appendChild( valueCell );
					table.appendChild( row );
				};

				addRow( tierTable, 'Server tier', serverTier, 'pmd-perf-good' );
				addRow(
					tierTable,
					'Client tier',
					clientTier,
					clientTier && serverTier && clientTier !== serverTier ? 'pmd-perf-warn' : 'pmd-perf-good'
				);
				panel.appendChild( tierTable );

				// Resolved (active) density values. chars_between includes any
				// matching override row from the repeater.
				var resolvedTitle = document.createElement( 'div' );
				resolvedTitle.className = 'pmd-ad-perf-subtitle';
				resolvedTitle.textContent = 'Resolved values';
				panel.appendChild( resolvedTitle );

				var resolvedTable = document.createElement( 'table' );
				resolvedTable.className = 'pmd-ad-perf-table';
				var resolvedLabels = {
					chars_first_with_image:    '1st (w/ image)',
					chars_first_without_image: '1st (no image)',
					chars_between:             'Between',
					min_chars_after_last_ad:   'Min after last'
				};
				Object.keys( resolvedLabels ).forEach( function( k ) {
					var label = resolvedLabels[ k ];
					if ( k === 'chars_between' && density.override ) {
						label += ' *';
					}
					addRow( resolvedTable, label, String( density.resolved[ k ] ) );
				} );
				panel.appendChild( resolvedTable );

				// Override match (when an author/category/post_type row matched).
				if ( density.override ) {
					var overrideTitle = document.createElement( 'div' );
					overrideTitle.className = 'pmd-ad-perf-subtitle';
					overrideTitle.textContent = '* Override match (chars between)';
					panel.appendChild( overrideTitle );

					var overrideTable = document.createElement( 'table' );
					overrideTable.className = 'pmd-ad-perf-table';
					addRow( overrideTable, 'Type',   String( density.override.type ) );
					addRow( overrideTable, 'Slug',   String( density.override.slug ) );
					addRow( overrideTable, 'Column', String( density.override.column ) );
					addRow( overrideTable, 'Value',  String( density.override.value ), 'pmd-perf-good' );
					panel.appendChild( overrideTable );
				}

				// Per-tier configured values. Highlights which tier is active and
				// shows '—' for fields inheriting from the default. Columns ordered
				// D → T → M to match the ACF admin UI (Large → Medium → Small).
				var perTierTitle = document.createElement( 'div' );
				perTierTitle.className = 'pmd-ad-perf-subtitle';
				perTierTitle.textContent = 'Per-tier configured (— = inherit)';
				panel.appendChild( perTierTitle );

				var perTierTable = document.createElement( 'table' );
				perTierTable.className = 'pmd-ad-perf-table';

				// Header row.
				var headerRow = document.createElement( 'tr' );
				[ '', 'D', 'T', 'M', 'def' ].forEach( function( h ) {
					var th = document.createElement( 'td' );
					th.textContent = h;
					th.style.fontWeight = '600';
					th.style.color = '#aaa';
					headerRow.appendChild( th );
				} );
				perTierTable.appendChild( headerRow );

				var shortLabels = {
					chars_first_with_image:    '1st img',
					chars_first_without_image: '1st no-img',
					chars_between:             'between',
					min_chars_after_last_ad:   'min after'
				};
				var tiers = density.tiers || {};
				var defaults = density.defaults || {};
				var fmt = function( v ) {
					return ( v === null || v === undefined ) ? '—' : String( v );
				};
				Object.keys( shortLabels ).forEach( function( k ) {
					var row = document.createElement( 'tr' );
					[
						shortLabels[ k ],
						fmt( tiers.desktop ? tiers.desktop[ k ] : null ),
						fmt( tiers.tablet  ? tiers.tablet[ k ]  : null ),
						fmt( tiers.mobile  ? tiers.mobile[ k ]  : null ),
						fmt( defaults[ k ] )
					].forEach( function( txt, idx ) {
						var cell = document.createElement( 'td' );
						cell.textContent = txt;
						// Highlight the active tier column.
						var tierForCol = [ null, 'desktop', 'tablet', 'mobile', null ][ idx ];
						if ( tierForCol && tierForCol === serverTier ) {
							cell.style.color = '#4ecdc4';
							cell.style.fontWeight = '600';
						}
						row.appendChild( cell );
					} );
					perTierTable.appendChild( row );
				} );
				panel.appendChild( perTierTable );
			}

			// Per-slot render section (rows added dynamically via updatePerfSlotRow).
			var slotTitle = document.createElement( 'div' );
			slotTitle.className = 'pmd-ad-perf-subtitle';
			slotTitle.textContent = 'Slot Render Times';
			panel.appendChild( slotTitle );

			var slotTable = document.createElement( 'table' );
			slotTable.className = 'pmd-ad-perf-table pmd-ad-perf-slot-table';
			panel.appendChild( slotTable );

			// Toggle button.
			var toggle = document.createElement( 'button' );
			toggle.className = 'pmd-ad-perf-toggle';
			toggle.textContent = 'Perf';
			toggle.addEventListener( 'click', function() {
				panel.classList.toggle( 'pmd-ad-perf-panel--collapsed' );
			} );

			document.body.appendChild( panel );
			document.body.appendChild( toggle );
		},

		/**
		 * Update or add a per-slot row in the perf timing panel.
		 *
		 * @param {string} divId Slot div ID.
		 */
		updatePerfSlotRow: function( divId ) {
			var slotTable = document.querySelector( '.pmd-ad-perf-slot-table' );
			if ( ! slotTable ) {
				return;
			}

			var t = this.perfTimings;
			var renderTime = t[ 'slot-rendered:' + divId ];
			var displayStart = t[ 'display-start' ];

			if ( renderTime === undefined || displayStart === undefined ) {
				return;
			}

			var ms = Math.round( renderTime - displayStart );

			var row = document.createElement( 'tr' );

			var labelCell = document.createElement( 'td' );
			labelCell.textContent = divId;
			row.appendChild( labelCell );

			var valueCell = document.createElement( 'td' );
			valueCell.textContent = ms + ' ms';
			valueCell.className = ms < 2000 ? 'pmd-perf-good' : ( ms < 4000 ? 'pmd-perf-warn' : 'pmd-perf-bad' );
			row.appendChild( valueCell );

			slotTable.appendChild( row );
		},

		/**
		 * Initialize debug overlays for ad slots.
		 * Shows slot ID, sizes, and char count (for content ads) for each ad slot.
		 * Overlay is placed above the ad container so it persists after ad loads.
		 */
		initDebugOverlays: function() {
			if ( ! this.isDebugMode() ) {
				return;
			}

			var self = this;
			var adContainers = document.querySelectorAll( '.pmd-ad-container' );
			var config = window.pmdAdsConfig || {};
			var adRefreshEnabled = config.adRefreshEnabled ? 1 : 0;
			var adRefreshMax = config.adRefreshMax || 0;
			var contentSlots = this.contentSlots || {};

			// Page-level density debug strip — sits at the top of body, styled like
			// the per-slot debug labels. Shows resolved viewport tier and the four
			// active density values. Server data comes from pmdAdsConfig.debug.density.
			self.createDensityDebugLabel();

			adContainers.forEach( function( container ) {
				// Check for regular ad slot first.
				var slot = container.querySelector( '.pmd-ad-slot[data-slot-id]' );
				var nativeSlot = container.querySelector( '.pmd-native-ad-slot' );

				// Handle native ad slots.
				if ( nativeSlot ) {
					self.createNativeAdDebugLabel( container, nativeSlot );
					return;
				}

				if ( ! slot ) {
					return;
				}

				var slotId = slot.getAttribute( 'data-slot-id' );
				var sizesDisplay = self.getSizesForCurrentViewport( slot );
				var charCountDisplay = '';

				// Get char count data for content ads (ad-in* slots).
				if ( slotId && slotId.indexOf( 'ad-in' ) === 0 && contentSlots[ slotId ] ) {
					var slotData = contentSlots[ slotId ];
					if ( slotData.charCount !== undefined ) {
						charCountDisplay = 'Chars: ' + slotData.charCount.toLocaleString();
						if ( slotData.charsSinceLast !== undefined ) {
							charCountDisplay += ' (gap: ' + slotData.charsSinceLast.toLocaleString() + ')';
						}
					}
				}

				// Create debug label - inserted before the container so it persists.
				var label = document.createElement( 'div' );
				label.className = 'pmd-ad-debug-label';

				// Build label content using DOM nodes (no innerHTML for security).
				var slotIdSpan = document.createElement( 'span' );
				slotIdSpan.className = 'pmd-ad-debug-slot-id';
				slotIdSpan.textContent = slotId;
				label.appendChild( slotIdSpan );

				if ( sizesDisplay ) {
					label.appendChild( document.createTextNode( ' ' ) );
					var sizesSpan = document.createElement( 'span' );
					sizesSpan.className = 'pmd-ad-debug-sizes';
					sizesSpan.textContent = sizesDisplay;
					label.appendChild( sizesSpan );
				}

				if ( charCountDisplay ) {
					label.appendChild( document.createTextNode( ' ' ) );
					var charsSpan = document.createElement( 'span' );
					charsSpan.className = 'pmd-ad-debug-chars';
					charsSpan.textContent = charCountDisplay;
					label.appendChild( charsSpan );
				}

				// Add refresh status.
				var refreshInterval = self.getSlotRefreshInterval( slot.id );
				label.appendChild( document.createTextNode( ' ' ) );
				var refreshSpan = document.createElement( 'span' );
				refreshSpan.className = 'pmd-ad-debug-refresh';
				refreshSpan.textContent = adRefreshEnabled ? 'Refresh: ' + refreshInterval + 's' + ( adRefreshMax ? ' (max ' + adRefreshMax + ')' : '' ) : 'Refresh: Off';
				label.appendChild( refreshSpan );

				// Insert label inside the ad container as first child.
				container.insertBefore( label, container.firstChild ); // phpcs:ignore WordPressVIPMinimum.JS.HTMLExecutingFunctions.insertBefore -- Inserting programmatically-built DOM element, no raw HTML.
			} );

			// Build the performance timing panel.
			this.buildPerfTimingPanel();
		},

		/**
		 * Create debug label for native ad slots.
		 *
		 * @param {Element} container The ad container element.
		 * @param {Element} nativeSlot The native ad slot element.
		 */
		createNativeAdDebugLabel: function( container, nativeSlot ) {
			var slotId = nativeSlot.getAttribute( 'data-slot-id' ) || 'ad-native';

			// Create debug label.
			var label = document.createElement( 'div' );
			label.className = 'pmd-ad-debug-label pmd-ad-debug-label--native';

			// Native ad badge.
			var badgeSpan = document.createElement( 'span' );
			badgeSpan.className = 'pmd-ad-debug-native-badge';
			badgeSpan.textContent = 'NATIVE';
			label.appendChild( badgeSpan );

			// Slot ID.
			label.appendChild( document.createTextNode( ' ' ) );
			var slotSpan = document.createElement( 'span' );
			slotSpan.className = 'pmd-ad-debug-slot-id';
			slotSpan.textContent = slotId;
			label.appendChild( slotSpan );

			// Insert label before the ad container.
			container.parentNode.insertBefore( label, container ); // phpcs:ignore WordPressVIPMinimum.JS.HTMLExecutingFunctions.insertBefore -- Inserting programmatically-built DOM element, no raw HTML.
		},

		/**
		 * Build a page-level "DENSITY" debug strip and prepend it to <body>.
		 *
		 * Mirrors the per-slot debug label style. Only renders when
		 * window.pmdAdsConfig.debug.density is present (server-gated by
		 * ?pmd_ads_debug=1).
		 */
		createDensityDebugLabel: function() {
			var debugCfg = ( window.pmdAdsConfig && window.pmdAdsConfig.debug ) || null;
			if ( ! debugCfg || ! debugCfg.density ) {
				return;
			}

			var density = debugCfg.density;
			var resolved = density.resolved || {};
			var serverTier = density.tier || '';
			var clientTier = document.documentElement.getAttribute( 'data-vp' ) || '';

			var label = document.createElement( 'div' );
			label.className = 'pmd-ad-debug-label pmd-ad-debug-label--density';

			// "DENSITY" badge (re-uses the native badge styling for visual parity).
			var badge = document.createElement( 'span' );
			badge.className = 'pmd-ad-debug-native-badge';
			badge.textContent = 'DENSITY';
			label.appendChild( badge );

			// Tier display: "server: desktop" or "server: desktop (client: mobile)" when divergent.
			label.appendChild( document.createTextNode( ' ' ) );
			var tierSpan = document.createElement( 'span' );
			tierSpan.className = 'pmd-ad-debug-slot-id';
			var tierText = 'tier: ' + ( serverTier || '?' );
			if ( clientTier && serverTier && clientTier !== serverTier ) {
				tierText += ' (client: ' + clientTier + ')';
			}
			tierSpan.textContent = tierText;
			label.appendChild( tierSpan );

			// Resolved values, formatted compact.
			var betweenText = String( resolved.chars_between );
			if ( density.override ) {
				// Mark the between value with an asterisk when it came from an override row.
				betweenText += '*';
			}

			var valuesSpan = document.createElement( 'span' );
			valuesSpan.className = 'pmd-ad-debug-sizes';
			valuesSpan.textContent =
				'1st-img: ' + ( resolved.chars_first_with_image )
				+ '  |  1st-no-img: ' + ( resolved.chars_first_without_image )
				+ '  |  between: ' + betweenText
				+ '  |  min-after: ' + ( resolved.min_chars_after_last_ad );
			label.appendChild( document.createTextNode( ' ' ) );
			label.appendChild( valuesSpan );

			// Override match details (e.g. "* override: category/sports @ tablet").
			if ( density.override ) {
				var overrideSpan = document.createElement( 'span' );
				overrideSpan.className = 'pmd-ad-debug-chars';
				overrideSpan.textContent =
					'* override: ' + density.override.type
					+ '/' + density.override.slug
					+ ' @ ' + density.override.column;
				label.appendChild( document.createTextNode( ' ' ) );
				label.appendChild( overrideSpan );
			}

			// Prepend to <body> so the strip is the first visible element.
			document.body.insertBefore( label, document.body.firstChild ); // phpcs:ignore WordPressVIPMinimum.JS.HTMLExecutingFunctions.insertBefore -- Programmatically-built DOM element, no raw HTML.
		},

		/**
		 * Get all native ad slots on the page.
		 *
		 * @return {NodeList} Collection of native ad slot elements.
		 */
		getNativeAdSlots: function() {
			return document.querySelectorAll( '.pmd-native-ad-slot' );
		},

		/**
		 * Get native ad slot by feed number.
		 *
		 * @param {number} feedNumber The feed number (1-15).
		 * @return {Element|null} The native ad slot element or null.
		 */
		getNativeAdSlotByFeed: function( feedNumber ) {
			return document.querySelector( '.pmd-native-ad-slot[data-feed="' + feedNumber + '"]' );
		},

		/**
		 * Mark a native ad slot as loaded and trigger resize.
		 * Call this after injecting content into a native ad slot.
		 *
		 * @param {Element} slot The native ad slot element.
		 */
		markNativeAdLoaded: function( slot ) {
			if ( ! slot ) {
				return;
			}

			slot.classList.add( 'pmd-native-ad-loaded' );

			// Dispatch custom event for external listeners.
			var event = new CustomEvent( 'pmdNativeAdLoaded', {
				detail: {
					feed: slot.getAttribute( 'data-feed' ),
					position: slot.getAttribute( 'data-position' ),
					slotPosition: slot.getAttribute( 'data-slot-position' ),
					element: slot
				},
				bubbles: true
			} );
			slot.dispatchEvent( event );
		},

		/**
		 * Inject content into a native ad slot.
		 * Automatically marks the slot as loaded after injection.
		 *
		 * @param {Element|number} slotOrFeed The slot element or feed number.
		 * @param {string|Element} content HTML string or DOM element to inject.
		 */
		injectNativeAdContent: function( slotOrFeed, content ) {
			var slot = typeof slotOrFeed === 'number'
				? this.getNativeAdSlotByFeed( slotOrFeed )
				: slotOrFeed;

			if ( ! slot ) {
				return;
			}

			// Clear existing content.
			while ( slot.firstChild ) {
				slot.removeChild( slot.firstChild );
			}

			// Inject new content.
			if ( typeof content === 'string' ) {
				var template      = document.createElement( 'template' );
				template.innerHTML = content; // phpcs:ignore WordPressVIPMinimum.JS.InnerHTML.Found -- Content is from GAM native ad response, inserted via template for safe DOM parsing.
				slot.appendChild( template.content );
			} else if ( content instanceof Element ) {
				slot.appendChild( content );
			}

			// Mark as loaded.
			this.markNativeAdLoaded( slot );
		}
	};

	// Initialize when DOM is ready.
	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', function() {
			window.pmdAds.controller.init();
		} );
	} else {
		window.pmdAds.controller.init();
	}

} )( window, document );
